import * as pc from 'playcanvas'
import {
  CommunityApi,
  mapTask,
  resolveAvailableModelFormats,
  resolveModelFormatCandidates
} from './api.js'
import {
  createAabbMovementBounds,
  readCameraDataFrames,
  readCameraDataMovementBounds,
  readCameraDataPose,
  readCameraDataTrajectory
} from './camera-data.js'
import { ModelEntrance } from './entrance.js'
import { loadGsplat } from './load-gsplat.js'
import { OrbitControls } from './orbit-controls.js'
import {
  mergeTaskModelMetadata,
  normalizeOrbitMotionType,
  readRenderTaskNumber,
  resolveViewerInteractionSpeeds,
  RENDER_TASK_DEFAULT_SETTINGS,
  RENDER_TASK_SETTING_RANGES,
  isImageTo3dgsTask,
  isSpatialModel,
  supportsArProjection,
  usesAabbOrbitCenter
} from './render-task-contract.js'
import { SurfacePicker } from './picker.js'
import { destroyRemotePlayCanvasLod, renderRemotePlayCanvasLod } from './remote-lod-renderer.js'
import { getServicePausedMessage } from './service-messages.js'
import { Annotation, AnnotationManager } from './annotations.mjs'
import {
  annotationAttachmentForServer,
  createAnnotationUploadSessionId,
  inferAnnotationAttachmentKind,
  normalizeAnnotationAttachments,
  normalizeAnnotationCameraPose,
  normalizeAnnotationTextColor,
  normalizeAnnotationTextSize,
  revokeLocalAnnotationAttachments
} from './annotation-utils.js'
import { DEFAULT_SKYBOX_PRESET, getSkyboxPreset } from './skybox-presets.js'
import { clearSurfaceRingMarker, showSurfaceRingMarker } from './surface-ring-marker.js'
import {
  orbitPositionAtProgress,
  resolveVideoRecordingMode,
  startCanvasVideoRecording,
  supportsCanvasVideoRecording
} from './video-recording.js'

const IMAGE_VIDEO_PERMANENT_FAILURE_CODES = new Set([
  'IMAGE_VIDEO_COPYRIGHT_REJECTED',
  'IMAGE_VIDEO_CONTENT_REJECTED'
])

const pickDepthGlsl = /* glsl */ `
vec4 packFloat(float depth) {
    uvec4 u = (uvec4(floatBitsToUint(depth)) >> uvec4(0u, 8u, 16u, 24u)) & 0xffu;
    return vec4(u) / 255.0;
}
vec4 encodePickOutput(uint id) {
    const vec4 inv = vec4(1.0 / 255.0);
    const uvec4 shifts = uvec4(16, 8, 0, 24);
    uvec4 col = (uvec4(id) >> shifts) & uvec4(0xff);
    return vec4(col) * inv;
}
vec4 getPickOutput() {
    return packFloat(gl_FragCoord.z);
}
#ifdef DEPTH_PICK_PASS
    #include "floatAsUintPS"
    #ifndef CAMERAPLANES
        #define CAMERAPLANES
        uniform vec4 camera_params;
    #endif
    vec4 getPickDepth() {
        float linearDepth;
        if (camera_params.w > 0.5) {
            linearDepth = gl_FragCoord.z;
        } else {
            float viewDist = 1.0 / gl_FragCoord.w;
            linearDepth = (viewDist - camera_params.z) / (camera_params.y - camera_params.z);
        }
        return float2uint(linearDepth);
    }
#endif
`

const pickDepthWgsl = /* wgsl */ `
fn packFloat(depth: f32) -> vec4f {
    let u: vec4<u32> = (vec4<u32>(bitcast<u32>(depth)) >> vec4<u32>(0u, 8u, 16u, 24u)) & vec4<u32>(0xffu);
    return vec4f(u) / 255.0;
}
fn encodePickOutput(id: u32) -> vec4f {
    let inv: vec4f = vec4f(1.0 / 255.0);
    let shifts: vec4u = vec4u(16u, 8u, 0u, 24u);
    let col: vec4u = (vec4u(id) >> shifts) & vec4u(0xffu);
    return vec4f(col) * inv;
}
fn getPickOutput() -> vec4f {
    return packFloat(pcPosition.z);
}
#ifdef DEPTH_PICK_PASS
    #include "floatAsUintPS"
    #ifndef CAMERAPLANES
        #define CAMERAPLANES
        uniform camera_params: vec4f;
    #endif
    fn getPickDepth() -> vec4f {
        var linearDepth: f32;
        if (uniform.camera_params.w > 0.5) {
            linearDepth = pcPosition.z;
        } else {
            let viewDist = 1.0 / pcPosition.w;
            linearDepth = (viewDist - uniform.camera_params.z) / (uniform.camera_params.y - uniform.camera_params.z);
        }
        return float2uint(linearDepth);
    }
#endif
`

function colorFromHex(value, fallback = '#000000') {
  const hex = /^#[\da-f]{6}$/i.test(String(value || '')) ? String(value) : fallback
  return new pc.Color(
    Number.parseInt(hex.slice(1, 3), 16) / 255,
    Number.parseInt(hex.slice(3, 5), 16) / 255,
    Number.parseInt(hex.slice(5, 7), 16) / 255,
    1
  )
}

function normalizeVector(value) {
  if (typeof value === 'string') {
    try {
      value = JSON.parse(value)
    } catch {
      return null
    }
  }
  if (!value) return null
  const x = Number(value.x ?? value[0])
  const y = Number(value.y ?? value[1])
  const z = Number(value.z ?? value[2])
  return [x, y, z].every(Number.isFinite) ? new pc.Vec3(x, y, z) : null
}

function firstVector(candidates) {
  for (const candidate of candidates) {
    const vector = normalizeVector(candidate)
    if (vector) return vector
  }
  return null
}

function normalizePose(value) {
  if (!value) return null
  const position = firstVector([
    value.cameraPosition,
    value.camera_position,
    value.position,
    value.camera?.position,
    value.pose?.position
  ])
  const focus = firstVector([
    value.focus,
    value.focusPoint,
    value.target,
    value.lookAt,
    value.look_at,
    value.camera?.focus,
    value.pose?.focus
  ])
  return position && focus ? { position, focus } : null
}

function computeFullModelBounds(entity, asset) {
  const instanceAabb = entity.gsplat?.instance?.meshInstance?.aabb
  if (instanceAabb?.center && instanceAabb?.halfExtents) {
    return {
      center: instanceAabb.center.clone(),
      halfExtents: instanceAabb.halfExtents.clone(),
      radius: Math.max(instanceAabb.halfExtents.length(), 0.05)
    }
  }

  const data = asset.resource?.gsplatData
  if (data?.calcAabb) {
    const aabb = new pc.BoundingBox()
    data.calcAabb(aabb)
    return {
      center: aabb.center.clone(),
      halfExtents: aabb.halfExtents.clone(),
      radius: Math.max(aabb.halfExtents.length(), 0.05)
    }
  }

  const xs = data?.getProp?.('x')
  const ys = data?.getProp?.('y')
  const zs = data?.getProp?.('z')
  const count = Number(data?.numSplats || xs?.length || 0)
  if (!xs || !ys || !zs || count <= 0) {
    return { center: new pc.Vec3(), halfExtents: new pc.Vec3(0.5, 0.5, 0.5), radius: 1 }
  }

  let minX = Infinity
  let minY = Infinity
  let minZ = Infinity
  let maxX = -Infinity
  let maxY = -Infinity
  let maxZ = -Infinity
  for (let index = 0; index < count; index += 1) {
    const x = xs[index]
    const y = ys[index]
    const z = zs[index]
    if (![x, y, z].every(Number.isFinite)) continue
    minX = Math.min(minX, x)
    minY = Math.min(minY, y)
    minZ = Math.min(minZ, z)
    maxX = Math.max(maxX, x)
    maxY = Math.max(maxY, y)
    maxZ = Math.max(maxZ, z)
  }
  if (!Number.isFinite(minX)) {
    return { center: new pc.Vec3(), halfExtents: new pc.Vec3(0.5, 0.5, 0.5), radius: 1 }
  }
  const min = new pc.Vec3(minX, minY, minZ)
  const max = new pc.Vec3(maxX, maxY, maxZ)
  const center = min.clone().add(max).mulScalar(0.5)
  const halfExtents = max.clone().sub(min).mulScalar(0.5)
  return { center, halfExtents, radius: Math.max(halfExtents.length(), 0.05) }
}

function readLodBounds(asset) {
  const aabb = asset?.resource?.aabb
  if (!aabb?.center || !aabb?.halfExtents) {
    throw new Error('PlayCanvas LOD manifest AABB is missing')
  }
  return {
    center: aabb.center.clone(),
    halfExtents: aabb.halfExtents.clone(),
    radius: Math.max(aabb.halfExtents.length(), 0.05)
  }
}

function publicError(error) {
  const rawMessage = error instanceof Error ? error.message : String(error || '模型加载失败')
  if (/^模型数据暂时无法读取/.test(rawMessage)) return rawMessage
  return '模型数据暂时无法读取，请稍后重试。'
}

function imageVideoFailureCode(error) {
  return String(
    error?.code ||
    error?.payload?.code ||
    error?.payload?.error?.code ||
    ''
  ).trim()
}

function isPermanentImageVideoFailure(error) {
  return error?.retryable === false ||
    error?.payload?.retryable === false ||
    error?.payload?.error?.retryable === false ||
    IMAGE_VIDEO_PERMANENT_FAILURE_CODES.has(imageVideoFailureCode(error))
}

function imageVideoFailureMessage(error, fallback) {
  const payloadError = error?.payload?.error
  const backendMessage = typeof payloadError === 'object' ? payloadError?.message : payloadError
  return String(
    (isPermanentImageVideoFailure(error) ? backendMessage : '') ||
    (isPermanentImageVideoFailure(error) ? error?.payload?.message : '') ||
    getServicePausedMessage(error) ||
    error?.message ||
    fallback
  ).trim() || fallback
}

function loadingErrorState(error) {
  const status = Number(error?.statusCode || error?.status || 0)
  const raw = error instanceof Error ? error.message : String(error || '')
  const offline = typeof navigator !== 'undefined' && navigator.onLine === false

  if (status === 401 || status === 403 || /无权|权限|未授权|private|forbidden/i.test(raw)) {
    return {
      kind: 'locked',
      title: '无法访问这个空间',
      text: '这个空间可能是私密内容，或当前登录状态已失效。',
      canRetry: false,
      canExit: true
    }
  }
  if (status === 404 || status === 410 || /缺少 taskId|not found|不存在|已删除|无法定位社区模型/i.test(raw)) {
    return {
      kind: 'empty',
      title: '找不到这个空间',
      text: '链接可能不完整，或者作品已经被删除。',
      canRetry: false,
      canExit: true
    }
  }
  if (status === 409 || status === 425 || /生成中|处理中|尚未生成|not ready/i.test(raw)) {
    return {
      kind: 'pending',
      title: '空间还在生成中',
      text: '模型数据还没有准备好，请稍后再试。',
      canRetry: true,
      canExit: true
    }
  }
  if (offline || /failed to fetch|network|网络|超时|timeout|load failed/i.test(raw)) {
    return {
      kind: 'network',
      title: offline ? '信号暂时走丢了' : '模型在路上绕了个弯',
      text: offline ? '连上网络后再试一次，模型会在这里等你。' : '部分模型数据还没赶到，检查网络后再试一次吧。',
      canRetry: true,
      canExit: true
    }
  }
  if ([429, 502, 503, 504].includes(status)) {
    return {
      kind: 'network',
      title: status === 429 ? '手速有点快啦' : '空间站正在忙碌',
      text: status === 429 ? '让服务器喘口气，稍等片刻再试一次。' : '模型正在排队出发，稍后再来敲敲门吧。',
      canRetry: true,
      canExit: true
    }
  }
  if (/webgl|graphics device|gpu|context lost|不支持.*渲染|无法.*渲染/i.test(raw)) {
    return {
      kind: 'unsupported',
      title: '当前设备无法渲染',
      text: '请更新微信或系统版本，也可以换一台支持图形加速的设备打开。',
      canRetry: false,
      canExit: true
    }
  }
  return {
    kind: 'error',
    title: '空间加载失败',
    text: publicError(error) || '模型数据暂时无法加载，请稍后重试。',
    canRetry: true,
    canExit: true
  }
}

function copyCanvasForUpload(source, maxEdge = 960) {
  const sourceWidth = Math.max(1, Number(source?.width) || 1)
  const sourceHeight = Math.max(1, Number(source?.height) || 1)
  const limit = pc.math.clamp(Number(maxEdge) || 960, 320, 1600)
  const scale = Math.min(1, limit / Math.max(sourceWidth, sourceHeight))
  const target = document.createElement('canvas')
  target.width = Math.max(14, Math.round(sourceWidth * scale))
  target.height = Math.max(14, Math.round(sourceHeight * scale))
  const context = target.getContext('2d', { alpha: false })
  if (!context) throw new Error('当前浏览器无法处理模型视角图')
  context.fillStyle = '#ffffff'
  context.fillRect(0, 0, target.width, target.height)
  context.drawImage(source, 0, 0, target.width, target.height)
  return target
}

function encodeJpeg(canvas, filename, quality = 0.78) {
  const safeQuality = pc.math.clamp(Number(quality) || 0.78, 0.55, 0.9)
  return new Promise((resolve, reject) => {
    const finish = blob => {
      if (!blob) {
        reject(new Error('模型视角图压缩失败'))
        return
      }
      try {
        resolve(new File([blob], filename, { type: 'image/jpeg', lastModified: Date.now() }))
      } catch {
        try {
          Object.defineProperty(blob, 'name', { value: filename })
        } catch {
          // FormData.append receives the filename separately when File is unavailable.
        }
        resolve(blob)
      }
    }

    if (typeof canvas.toBlob === 'function') {
      canvas.toBlob(finish, 'image/jpeg', safeQuality)
      return
    }
    try {
      fetch(canvas.toDataURL('image/jpeg', safeQuality))
        .then(response => response.blob())
        .then(finish, reject)
    } catch (error) {
      reject(error)
    }
  })
}

export class ViewerRuntime {
  constructor({ root, canvas, config, callbacks = {} }) {
    this.root = root
    this.canvas = canvas
    this.config = config
    this.callbacks = callbacks
    this.api = new CommunityApi(config.apiBase, config.authToken, {
      project: config.viewMode === 'project',
      miniappClientId: config.miniappClientId
    })
    this.task = null
    this.meta = null
    this.setting = {}
    this.cameraData = null
    this.app = null
    this.camera = null
    this.model = null
    this.asset = null
    this.lodSession = null
    this.controls = null
    this.picker = null
    this.entrance = null
    this.surfaceRing = null
    this.annotationManager = null
    this.annotationManagerEntity = null
    this.annotationEntities = []
    this.annotationVisible = true
    this.annotationExpanded = false
    this.annotationCallout = false
    this.annotationEditMode = false
    this.annotationReadOnly = config.viewMode !== 'project'
    this.annotationBaseline = '[]'
    this.annotationEditSnapshot = null
    this.pendingAnnotationSave = null
    this.pendingInitialCameraSave = false
    this.bounds = null
    this.initialPose = null
    this.cameraMovementBounds = null
    this.cameraTrajectory = []
    this.cameraTrajectoryPlayback = null
    this.videoRecording = null
    this.videoRecordingStarting = false
    this.recordedVideo = null
    this.recordedVideoUrl = ''
    this.recordedVideoSaving = false
    this.imageVideoGeneration = null
    this.imageVideoLookupPromise = null
    this.imageVideoLookupController = null
    this.imageVideoLookupState = 'unknown'
    this.imageVideoVersion = ''
    this.imageVideoVersionRevision = null
    this.imageVideoVersionPromise = null
    this.pendingImageVideoTaskId = ''
    this.generatedImageVideo = null
    this.imageVideoPanelOpen = false
    this.imageVideoIssue = null
    this.generatedImageVideoSaving = false
    this.orbitMotionType = RENDER_TASK_DEFAULT_SETTINGS.orbitMotionType
    this.motionData = null
    this.resizeObserver = null
    this.loading = false
    this.loaded = false
    this.destroyed = false
    this.focusPending = false
    this.format = 'sog'
    this.softwareLandscape = false
    this.tourActive = false
    this.readyTimer = 0
    this.readyEmitted = false
    this.activePointers = new Set()
    this.multiPointerGesture = false
    this.pointerDown = null
    this.tap = null
    this.tapTimer = 0
    this.annotationTouch = null
    this.annotationTouchWasMulti = false
    this.nativeTouchTap = null
    this.lastAnnotationActivation = null
    this.background = RENDER_TASK_DEFAULT_SETTINGS.backgroundColor
    this.annotationMenuEntity = null
    this.skyboxEnabled = false
    this.skyboxPreset = DEFAULT_SKYBOX_PRESET
    this.skyboxTextures = new Map()
    this.skyboxLoading = new Map()

    this.handlePointerDown = this.handlePointerDown.bind(this)
    this.handlePointerCancel = this.handlePointerCancel.bind(this)
    this.handlePointerUp = this.handlePointerUp.bind(this)
    this.handleDoubleClick = this.handleDoubleClick.bind(this)
    this.handleAnnotationTouchStart = this.handleAnnotationTouchStart.bind(this)
    this.handleAnnotationTouchMove = this.handleAnnotationTouchMove.bind(this)
    this.handleAnnotationTouchEnd = this.handleAnnotationTouchEnd.bind(this)
    this.handleAnnotationTouchCancel = this.handleAnnotationTouchCancel.bind(this)
    this.handleAnnotationClickCapture = this.handleAnnotationClickCapture.bind(this)
    this.handleAnnotationClick = this.handleAnnotationClick.bind(this)
    this.handleAnnotationAttachmentPreview = this.handleAnnotationAttachmentPreview.bind(this)
    this.handleAnnotationRemove = this.handleAnnotationRemove.bind(this)
    this.handleVisibilityChange = this.handleVisibilityChange.bind(this)
    this.resize = this.resize.bind(this)
    this.canvas.addEventListener('pointerdown', this.handlePointerDown, true)
    this.canvas.addEventListener('pointercancel', this.handlePointerCancel, true)
    this.canvas.addEventListener('pointerup', this.handlePointerUp, true)
    this.canvas.addEventListener('dblclick', this.handleDoubleClick, true)
    // WeChat WebView can skip Pointer Events and the synthetic click for a
    // transparent hotspot inside the CSS-rotated landscape viewer. Capture the
    // real touch on the viewer root so canvas and hotspot targets share one
    // reliable annotation hit path.
    this.root.addEventListener('touchstart', this.handleAnnotationTouchStart, { capture: true, passive: true })
    this.root.addEventListener('touchmove', this.handleAnnotationTouchMove, { capture: true, passive: true })
    this.root.addEventListener('touchend', this.handleAnnotationTouchEnd, { capture: true, passive: false })
    this.root.addEventListener('touchcancel', this.handleAnnotationTouchCancel, { capture: true, passive: true })
    this.root.addEventListener('click', this.handleAnnotationClickCapture, true)
    document.addEventListener('visibilitychange', this.handleVisibilityChange)
  }

  emit(name, ...args) {
    this.callbacks[name]?.(...args)
  }

  getVideoRecordingMode() {
    return resolveVideoRecordingMode(this.task, this.cameraTrajectory.length)
  }

  emitVideoRecordingState(state = {}) {
    this.emit('onVideoRecording', {
      available: Boolean(this.getVideoRecordingMode() && supportsCanvasVideoRecording(this.canvas)),
      mode: this.getVideoRecordingMode(),
      status: this.videoRecording ? (this.videoRecording.finishing ? 'processing' : 'recording') : 'idle',
      progress: 0,
      url: this.recordedVideoUrl,
      filename: this.recordedVideo?.filename || '',
      saving: this.recordedVideoSaving,
      saveProgress: 0,
      savingAction: '',
      ...state
    })
  }

  emitImageVideoState(state = {}) {
    const { status, ...details } = state
    const taskStatus = status || this.imageVideoGeneration?.status ||
      (this.generatedImageVideo ? 'ready' : this.imageVideoIssue ? 'error' : 'idle')
    this.emit('onImageVideo', {
      status: this.imageVideoPanelOpen ? taskStatus : 'idle',
      taskStatus,
      stageText: this.imageVideoGeneration?.stageText || '',
      taskId: this.imageVideoGeneration?.taskId || this.generatedImageVideo?.taskId || '',
      url: this.generatedImageVideo?.url || '',
      expiresAt: this.generatedImageVideo?.expiresAt || 0,
      saving: this.generatedImageVideoSaving,
      savingAction: '',
      error: this.imageVideoIssue?.message || '',
      retryable: this.imageVideoIssue?.retryable !== false,
      resumeOnly: this.imageVideoIssue?.resumeOnly ?? Boolean(this.pendingImageVideoTaskId || this.imageVideoLookupState === 'error'),
      canPause: Boolean(this.imageVideoGeneration?.taskId),
      ...details
    })
  }

  handleVisibilityChange() {
    if (document.hidden && this.videoRecording) void this.cancelVideoRecording({ silent: true })
  }

  handlePointerDown(event) {
    this.activePointers.add(event.pointerId)
    if (this.activePointers.size > 1) {
      this.multiPointerGesture = true
      this.pointerDown = null
      this.tap = null
      return
    }
    this.pointerDown = { id: event.pointerId, x: event.clientX, y: event.clientY, time: performance.now() }
  }

  handlePointerCancel(event) {
    this.activePointers.delete(event.pointerId)
    if (this.multiPointerGesture && !this.activePointers.size) this.multiPointerGesture = false
    if (this.pointerDown?.id === event.pointerId) this.pointerDown = null
  }

  handlePointerUp(event) {
    this.activePointers.delete(event.pointerId)
    if (this.multiPointerGesture) {
      if (!this.activePointers.size) this.multiPointerGesture = false
      this.pointerDown = null
      return
    }
    if (this.activePointers.size || !this.pointerDown || this.pointerDown.id !== event.pointerId) return
    const now = performance.now()
    const distance = Math.hypot(event.clientX - this.pointerDown.x, event.clientY - this.pointerDown.y)
    this.pointerDown = null
    if (distance > 7) {
      this.tap = null
      return
    }
    if (this.activateAnnotationAtClientPoint(event.clientX, event.clientY, 'pointer')) {
      this.tap = null
      window.clearTimeout(this.tapTimer)
      return
    }
    if (this.annotationEditMode) {
      this.tap = null
      window.clearTimeout(this.tapTimer)
      this.createAnnotationAtClientPoint(event.clientX, event.clientY)
      return
    }
    // The reference LOD viewer uses the browser's native dblclick event. Keep
    // the pointer-up fallback only for touch, where mini-program WebViews may
    // not dispatch dblclick at all.
    const useNativeLodDoubleClick = Boolean(this.lodSession) && event.pointerType !== 'touch'
    const isDouble = !useNativeLodDoubleClick && this.tap && now - this.tap.time < 360 &&
      Math.hypot(event.clientX - this.tap.x, event.clientY - this.tap.y) < 24
    if (isDouble) {
      this.tap = null
      window.clearTimeout(this.tapTimer)
      this.focusFromClientPoint(event.clientX, event.clientY)
      return
    }
    this.tap = { x: event.clientX, y: event.clientY, time: now }
    window.clearTimeout(this.tapTimer)
    this.tapTimer = window.setTimeout(() => {
      if (this.tap?.time !== now || this.tourActive) return
      this.emit('onCanvasTap')
      this.tap = null
    }, 370)
  }

  handleDoubleClick(event) {
    if (!this.lodSession || this.annotationEditMode || event.button > 0) return
    if (this.findAnnotationAtClientPoint(event.clientX, event.clientY)) return
    this.tap = null
    this.nativeTouchTap = null
    window.clearTimeout(this.tapTimer)
    event.preventDefault()
    void this.focusFromClientPoint(event.clientX, event.clientY)
  }

  annotationForHotspotTarget(target) {
    const hotspot = target?.closest?.('.pc-annotation-hotspot')
    if (!hotspot || !this.root.contains(hotspot) || !this.annotationManager) return null
    for (const [annotation, resources] of this.annotationManager._annotationResources || []) {
      if (resources?.hotspotDom === hotspot) return annotation
    }
    return null
  }

  isAnnotationSurfaceTarget(target) {
    return target === this.canvas || target === this.root || target?.classList?.contains('viewer__gesture-target')
  }

  handleAnnotationTouchStart(event) {
    const touches = event.touches
    if (!touches || touches.length !== 1) {
      this.annotationTouch = null
      if (touches?.length > 1) {
        this.annotationTouchWasMulti = true
        this.nativeTouchTap = null
      }
      return
    }
    if (this.annotationTouchWasMulti) return
    const touch = touches[0]
    this.annotationTouch = {
      id: touch.identifier,
      x: touch.clientX,
      y: touch.clientY,
      time: performance.now(),
      target: event.target
    }
  }

  handleAnnotationTouchCancel(event) {
    this.annotationTouch = null
    this.nativeTouchTap = null
    if (!event.touches?.length) this.annotationTouchWasMulti = false
  }

  handleAnnotationTouchMove(event) {
    if (event.touches?.length > 1) {
      this.annotationTouch = null
      this.annotationTouchWasMulti = true
      this.nativeTouchTap = null
      return
    }
    const pending = this.annotationTouch
    if (!pending) return
    const touch = Array.from(event.touches || []).find(item => item.identifier === pending.id)
    if (touch && Math.hypot(touch.clientX - pending.x, touch.clientY - pending.y) > 12) {
      this.annotationTouch = null
      this.nativeTouchTap = null
    }
  }

  handleAnnotationTouchEnd(event) {
    const pending = this.annotationTouch
    this.annotationTouch = null
    if (this.annotationTouchWasMulti) {
      if (!event.touches?.length) this.annotationTouchWasMulti = false
      return
    }
    if (!pending || event.touches?.length) return

    const touch = Array.from(event.changedTouches || []).find(item => item.identifier === pending.id)
    if (!touch) return
    const now = performance.now()
    if (now - pending.time > 900 || Math.hypot(touch.clientX - pending.x, touch.clientY - pending.y) > 12) {
      this.nativeTouchTap = null
      return
    }

    const isSurfaceTarget = this.isAnnotationSurfaceTarget(pending.target)
    let annotation = this.annotationForHotspotTarget(pending.target)
    if (!annotation && isSurfaceTarget) {
      annotation = this.findAnnotationAtClientPoint(touch.clientX, touch.clientY)
    }
    if (!annotation) {
      if (!isSurfaceTarget || !this.lodSession || this.annotationEditMode) return
      const previousTap = this.nativeTouchTap
      const isDoubleTap = previousTap && now - previousTap.time < 360 &&
        Math.hypot(touch.clientX - previousTap.x, touch.clientY - previousTap.y) < 24
      if (!isDoubleTap) {
        this.nativeTouchTap = { x: touch.clientX, y: touch.clientY, time: now }
        return
      }

      this.nativeTouchTap = null
      this.pointerDown = null
      this.tap = null
      window.clearTimeout(this.tapTimer)
      if (event.cancelable) event.preventDefault()
      if (!this.focusPending) void this.focusFromClientPoint(touch.clientX, touch.clientY)
      return
    }

    this.nativeTouchTap = null
    const recent = this.lastAnnotationActivation
    const alreadyHandled = recent?.annotation === annotation && now - recent.time < 160
    if (!alreadyHandled) this.activateAnnotation(annotation, touch.clientX, touch.clientY, now, 'touch')
    else this.rememberAnnotationActivation(annotation, touch.clientX, touch.clientY, now, 'touch')

    // Do not stop propagation: PlayCanvas still needs touchend to clear its
    // gesture state. preventDefault only suppresses the duplicate mouse click.
    if (event.cancelable) event.preventDefault()
    this.pointerDown = null
    this.tap = null
    window.clearTimeout(this.tapTimer)
  }

  handleAnnotationClickCapture(event) {
    const now = performance.now()
    const recent = this.lastAnnotationActivation
    let annotation = this.annotationForHotspotTarget(event.target)
    if (!annotation && this.isAnnotationSurfaceTarget(event.target)) {
      annotation = this.findAnnotationAtClientPoint(event.clientX, event.clientY)
    }
    if (
      recent && recent.source !== 'click' && now - recent.time < 900 &&
      (annotation || this.isAnnotationSurfaceTarget(event.target)) &&
      (annotation === recent.annotation || Math.hypot(event.clientX - recent.clientX, event.clientY - recent.clientY) < 40)
    ) {
      event.preventDefault()
      event.stopPropagation()
      event.stopImmediatePropagation?.()
      return
    }

    if (!annotation) return

    // Handle the click here and keep it away from the hotspot's own click
    // listener; otherwise the same annotation would immediately toggle closed.
    event.preventDefault()
    event.stopPropagation()
    event.stopImmediatePropagation?.()
    this.activateAnnotation(annotation, event.clientX, event.clientY, now, 'click')
  }

  setLoading(title, text, progress = null, indeterminate = progress === null, fileProgress = null, options = {}) {
    if (this.config.debugUi) return
    this.emit('onLoading', {
      visible: true,
      error: false,
      kind: 'loading',
      title,
      text,
      progress: progress === null ? undefined : Math.max(0, Math.min(100, progress)),
      indeterminate: Boolean(indeterminate),
      showProgressDetails: options.showProgressDetails !== false,
      ...(fileProgress
        ? {
            loadedBytes: Math.max(0, Number(fileProgress.loaded) || 0),
            totalBytes: Math.max(0, Number(fileProgress.total) || 0)
          }
        : {}),
      canRetry: false,
      canExit: false
    })
  }

  setError(error) {
    this.loaded = false
    this.emit('onLoaded', false)
    const state = loadingErrorState(error)
    this.emit('onLoading', this.config.debugUi
      ? { visible: false }
      : {
          visible: true,
          error: true,
          kind: state.kind,
          title: state.title,
          text: state.text,
          progress: 100,
          indeterminate: false,
          canRetry: state.canRetry,
          canExit: state.canExit
        })
  }

  finishLoading() {
    if (this.readyEmitted) return
    this.readyEmitted = true
    this.emit('onLoaded', true)
    this.emit('onLoading', { progress: 100, indeterminate: false, canExit: false })
    window.clearTimeout(this.readyTimer)
    this.readyTimer = window.setTimeout(() => {
      this.emit('onLoading', { visible: false })
      this.emit('onReady')
    }, this.config.debugUi ? 0 : 220)
  }

  async createViewer() {
    if (this.app) return
    const device = await pc.createGraphicsDevice(this.canvas, {
      deviceTypes: ['webgl2'],
      antialias: false,
      alpha: false,
      preserveDrawingBuffer: true
    })
    if (this.destroyed) {
      device.destroy?.()
      return
    }
    device.maxPixelRatio = Math.min(window.devicePixelRatio || 1, 2)

    const options = new pc.AppOptions()
    options.graphicsDevice = device
    options.mouse = new pc.Mouse(this.canvas)
    options.touch = new pc.TouchDevice(this.canvas)
    options.keyboard = new pc.Keyboard(window)
    options.componentSystems = [
      pc.RenderComponentSystem,
      pc.CameraComponentSystem,
      pc.LightComponentSystem,
      pc.GSplatComponentSystem,
      pc.ScriptComponentSystem
    ]
    options.resourceHandlers = [pc.TextureHandler, pc.GSplatHandler, pc.ScriptHandler]

    const app = new pc.AppBase(this.canvas)
    app.init(options)
    app.onLibrariesLoaded?.()
    app.setCanvasFillMode(pc.FILLMODE_NONE)
    app.setCanvasResolution(pc.RESOLUTION_FIXED)
    pc.ShaderChunks.get(app.graphicsDevice, 'glsl').set('pickPS', pickDepthGlsl)
    pc.ShaderChunks.get(app.graphicsDevice, 'wgsl').set('pickPS', pickDepthWgsl)
    app.scene.ambientLight = new pc.Color(1, 1, 1)

    const camera = new pc.Entity('miniapp-camera')
    camera.addComponent('camera', {
      clearColor: colorFromHex(RENDER_TASK_DEFAULT_SETTINGS.backgroundColor),
      clearColorBuffer: true,
      clearDepthBuffer: true,
      fov: 75,
      nearClip: 0.005,
      farClip: 1000000,
      frustumCulling: false
    })
    app.root.addChild(camera)

    const controls = new OrbitControls(this.canvas, camera)
    controls.setSoftwareLandscape(this.softwareLandscape)
    controls.onAutoRotateChange = active => this.emit('onAutoRotate', active)
    controls.onInteraction = () => this.emit('onInteraction')
    controls.enabled = !this.tourActive
    app.on('update', dt => controls.update(dt))
    app.on('update', dt => this.updateCameraTrajectory(dt))
    app.on('update', dt => this.updateVideoRecording(dt))
    app.on('update', () => this.updateAnnotationMenuPosition())
    app.start()

    this.app = app
    this.camera = camera
    this.controls = controls
    this.picker = new SurfacePicker(app, camera)
    app.on('annotation:click', this.handleAnnotationClick)
    app.on('annotation:attachmentPreview', this.handleAnnotationAttachmentPreview)
    app.on('annotation:remove', this.handleAnnotationRemove)
    this.entrance = new ModelEntrance({ app, root: this.root, camera, controls })
    this.resizeObserver = new ResizeObserver(this.resize)
    this.resizeObserver.observe(this.root)
    window.addEventListener('resize', this.resize)
    this.resize()
  }

  updateCameraTrajectory(dt) {
    const playback = this.cameraTrajectoryPlayback
    if (!playback || !this.controls) return
    const safeDt = Math.max(0, Math.min(Number(dt) || 1 / 60, 0.1))
    playback.elapsed += safeDt
    while (playback.index < this.cameraTrajectory.length - 1) {
      const current = this.cameraTrajectory[playback.index]
      if (playback.elapsed < current.duration) break
      playback.elapsed -= current.duration
      playback.index += 1
    }

    const current = this.cameraTrajectory[playback.index]
    const next = this.cameraTrajectory[playback.index + 1]
    if (!next) {
      this.controls.setPose(current.focus, current.position, { immediate: true, constrain: false })
      this.stopCameraTrajectory()
      return
    }

    const progress = Math.max(0, Math.min(1, playback.elapsed / Math.max(current.duration, 0.001)))
    const position = current.position.clone().lerp(current.position, next.position, progress)
    const focus = current.focus.clone().lerp(current.focus, next.focus, progress)
    this.controls.setPose(focus, position, { immediate: true, constrain: false })
  }

  updateVideoRecording(dt) {
    const recording = this.videoRecording
    if (!recording || recording.finishing || !this.controls) return
    const safeDt = Math.max(0, Math.min(Number(dt) || 1 / 60, 0.1))
    recording.elapsed = Math.min(recording.duration, recording.elapsed + safeDt)
    const progress = Math.min(1, recording.elapsed / Math.max(recording.duration, 0.001))

    if (recording.mode === 'orbit') {
      const position = orbitPositionAtProgress(recording.startPose.position, recording.startPose.focus, progress)
      this.controls.setPose(recording.startPose.focus, position, { immediate: true, constrain: false })
    } else {
      let remaining = recording.elapsed
      let index = 0
      while (index < this.cameraTrajectory.length - 1 && remaining >= this.cameraTrajectory[index].duration) {
        remaining -= this.cameraTrajectory[index].duration
        index += 1
      }
      const current = this.cameraTrajectory[index]
      const next = this.cameraTrajectory[index + 1]
      if (next) {
        const segmentProgress = Math.min(1, remaining / Math.max(current.duration, 0.001))
        const position = current.position.clone().lerp(current.position, next.position, segmentProgress)
        const focus = current.focus.clone().lerp(current.focus, next.focus, segmentProgress)
        this.controls.setPose(focus, position, { immediate: true, constrain: false })
      } else if (current) {
        this.controls.setPose(current.focus, current.position, { immediate: true, constrain: false })
      }
    }

    this.emitVideoRecordingState({ status: 'recording', progress: progress * 100 })
    if (progress >= 1) void this.finishVideoRecording()
  }

  resize() {
    if (!this.app || !this.camera?.camera) return
    const rect = this.root.getBoundingClientRect()
    // clientWidth/clientHeight keep the viewer's logical dimensions when the
    // portrait WebView uses the CSS-rotation landscape fallback. A transformed
    // bounding box reports portrait dimensions and would stretch the canvas.
    const width = Math.max(1, Math.round(this.root.clientWidth || rect.width))
    const height = Math.max(1, Math.round(this.root.clientHeight || rect.height))
    this.app.graphicsDevice.resizeCanvas(width, height)
    this.camera.camera.aspectRatio = width / height
  }

  getInitialCameraPose() {
    const savedPose = normalizePose(this.setting?.initialCameraPose)
    if (savedPose) return savedPose

    const pose = readCameraDataPose(this.cameraData, { bounds: this.bounds })
    if (!pose) return null

    // With no user-saved view, background-removed and AI models use the model
    // AABB as the stable focus for the automatically captured camera pose.
    if (this.bounds?.center && usesAabbOrbitCenter(this.task) && !this.config.activity) {
      return {
        position: pose.position.clone(),
        focus: this.bounds.center.clone(),
        focusSource: 'aabb_override'
      }
    }
    return pose
  }

  async pickCameraDataModelFocus() {
    if (!this.picker || !this.controls || !this.cameraData || !this.model || !this.canvas) return null
    const frames = readCameraDataFrames(this.cameraData)
    if (!frames.length) return null
    const first = frames[0]
    const savedPose = this.controls.getPose?.()
    const width = Math.max(1, Number(this.canvas.clientWidth || this.canvas.width || 1))
    const height = Math.max(1, Number(this.canvas.clientHeight || this.canvas.height || 1))
    const probeDistance = Math.max(Number(this.bounds?.radius || 1) * 4, 1)
    try {
      for (const frame of frames) {
        const direction = frame.direction.clone().normalize()
        const probeFocus = frame.position.clone().add(direction.clone().mulScalar(probeDistance))
        this.controls.setPose(probeFocus, frame.position, { immediate: true, constrain: false })
        const hit = await this.picker.pick(
          width * 0.5,
          height * 0.5,
          width,
          height,
          null,
          512,
          { depth: true }
        )
        if (!hit) continue
        const toHit = hit.clone().sub(frame.position)
        if (toHit.length() > 1e-6 && direction.dot(toHit) > 1e-6) {
          return { position: first.position.clone(), focus: hit, focusSource: 'model_depth' }
        }
      }
    } catch (error) {
      console.warn('[MeTop viewer] cameraData depth pick unavailable', error)
    } finally {
      if (savedPose?.position && savedPose?.focus) {
        this.controls.setPose(savedPose.focus, savedPose.position, { immediate: true, constrain: false })
      }
    }
    return null
  }

  applyCameraMovementBounds() {
    if (!this.controls) return false
    const imageTo3dgs = isImageTo3dgsTask(this.task)
    const spatialModel = isSpatialModel(this.task)
    this.controls.setFreeOrbit?.(spatialModel)
    if (spatialModel) {
      this.cameraMovementBounds = null
      this.controls.clearMovementBounds()
      return false
    }
    const centerOverride = this.bounds?.center && usesAabbOrbitCenter(this.task) && !this.config.activity
      ? this.bounds.center
      : null
    const cameraDataBounds = readCameraDataMovementBounds(this.cameraData, {
      center: centerOverride
    })
    const movementBounds = cameraDataBounds || createAabbMovementBounds(this.bounds)
    if (movementBounds && imageTo3dgs) {
      // Distance remains free even when cameraData is temporarily unavailable
      // and the viewer has to use its AABB safety fallback.
      movementBounds.constrainRadius = false
      // Only a cameraData-derived center is valid as the single-image orbit
      // focus; never lock the orbit onto the AABB fallback center.
      if (cameraDataBounds) {
        movementBounds.lockOrbitFocus = true
        movementBounds.limitFlyOrientation = true
      }
    }

    if (!movementBounds) {
      this.cameraMovementBounds = null
      this.controls.clearMovementBounds()
      return false
    }

    const applied = this.controls.setMovementBounds(movementBounds)
    this.cameraMovementBounds = applied ? this.controls.getMovementBounds() : null
    return applied
  }

  applyViewerSettings() {
    if (!this.controls || !this.camera?.camera) return
    const defaults = RENDER_TASK_DEFAULT_SETTINGS
    const ranges = RENDER_TASK_SETTING_RANGES
    const spatialModel = isSpatialModel(this.task)
    const interactionSpeeds = resolveViewerInteractionSpeeds({
      embedded: this.config.embedded,
      spatialModel,
      imageTo3dgs: isImageTo3dgsTask(this.task),
      moveSpeed: this.setting?.moveSpeed,
      pinchSpeed: this.setting?.pinchSpeed
    })
    this.controls.moveSpeed = interactionSpeeds.moveSpeed
    this.controls.orbitSpeed = readRenderTaskNumber(this.setting?.orbitSpeed, defaults.orbitSpeed, ranges.orbitSpeed)
    this.controls.autoRotateSpeed = readRenderTaskNumber(
      this.setting?.autoRotateSpeed,
      defaults.autoRotateSpeed,
      ranges.autoRotateSpeed
    )
    this.controls.pinchSpeed = interactionSpeeds.pinchSpeed
    this.camera.camera.fov = readRenderTaskNumber(
      this.setting?.originalFov ?? this.setting?.fov,
      defaults.originalFov,
      ranges.originalFov
    )
    this.orbitMotionType = normalizeOrbitMotionType(this.setting?.orbitMotionType)
    this.controls.setAutoRotateMotionType(this.orbitMotionType)
    const motionData = this.setting?.motionData
    this.motionData = Array.isArray(motionData?.keyframes) && motionData.keyframes.length >= 2
      ? { ...motionData, continuousPlayback: motionData.continuousPlayback !== false }
      : null

    const background = this.setting?.backgroundColor ?? defaults.backgroundColor
    const skyboxEnabled = Boolean(this.setting?.skyboxEnabled)
    const skyboxPreset = getSkyboxPreset(this.setting?.skyboxPreset)?.id || DEFAULT_SKYBOX_PRESET
    this.setBackground(background, false)
    this.skyboxPreset = skyboxPreset
    this.setSkyboxEnabled(skyboxEnabled, false)
    this.emit('onSettings', {
      fov: Math.round(this.camera.camera.fov),
      speed: Math.round(this.controls.orbitSpeed),
      background: this.background,
      moveSpeed: this.controls.moveSpeed,
      orbitSpeed: this.controls.orbitSpeed,
      autoRotateSpeed: this.controls.autoRotateSpeed,
      pinchSpeed: this.controls.pinchSpeed,
      orbitMotionType: this.orbitMotionType,
      motionData: this.motionData,
      initialCameraPose: this.setting?.initialCameraPose ?? null,
      skyboxEnabled,
      skyboxPreset
    })
  }

  async applyInitialPose() {
    if (!this.controls) return
    this.applyViewerSettings()
    this.controls.setFreeOrbit?.(isSpatialModel(this.task))
    if (!this.bounds) return
    const zoomOptions = { unrestrictedZoom: isImageTo3dgsTask(this.task) }
    let pose = this.getInitialCameraPose()
    const hasSavedPose = Boolean(normalizePose(this.setting?.initialCameraPose))
    if (pose && !hasSavedPose && !['explicit', 'camera_intersection', 'aabb_override'].includes(pose.focusSource)) {
      const depthPose = await this.pickCameraDataModelFocus()
      if (depthPose) pose = Object.assign({}, pose, depthPose)
    }

    if (pose) {
      this.controls.setPose(pose.focus, pose.position)
      this.controls.setBounds(this.bounds.radius, pose.position.distance(pose.focus), zoomOptions)
      this.applyCameraMovementBounds()
      const constrainedPose = this.controls.getPose()
      this.initialPose = {
        focus: constrainedPose.focus,
        position: constrainedPose.position
      }
      return
    }

    const focus = this.bounds.center.clone()
    const halfExtents = this.bounds.halfExtents || new pc.Vec3(this.bounds.radius, this.bounds.radius, this.bounds.radius)
    const modelRadius = Math.max(halfExtents.x, halfExtents.y, halfExtents.z, 0.05)
    const fitDistance = modelRadius / Math.sin(0.5 * this.camera.camera.fov * pc.math.DEG_TO_RAD)
    const mobilePadding = matchMedia('(pointer: coarse)').matches ? 1.8 : 1
    const position = focus.clone().add(new pc.Vec3(0, halfExtents.y, fitDistance * mobilePadding))
    this.controls.setPose(focus, position)
    this.controls.setBounds(this.bounds.radius, position.distance(focus), zoomOptions)
    this.applyCameraMovementBounds()
    const constrainedPose = this.controls.getPose()
    this.initialPose = {
      focus: constrainedPose.focus,
      position: constrainedPose.position
    }
  }

  clearModel() {
    this.clearAnnotations()
    this.destroyAnnotationManager()
    this.entrance?.cancel()
    this.emit('onEntrance', false)
    this.surfaceRing = clearSurfaceRingMarker(this.surfaceRing)
    const lodSession = this.lodSession
    this.lodSession = null
    if (lodSession) {
      destroyRemotePlayCanvasLod(lodSession)
    } else {
      this.model?.destroy?.()
      if (this.asset && this.app) {
        this.app.assets.remove(this.asset)
        this.asset.unload?.()
      }
    }
    this.model = null
    this.asset = null
    this.cameraMovementBounds = null
    this.controls?.clearMovementBounds()
    this.bounds = null
    this.initialPose = null
  }

  async loadLodModel(manifestUrl) {
    this.clearModel()
    this.setLoading('加载中', '', null, true, null, { showProgressDetails: false })
    this.applyViewerSettings()
    const initialPose = this.getInitialCameraPose()
    const session = await renderRemotePlayCanvasLod({
      app: this.app,
      manifestUrl,
      apiBaseUrl: this.config.apiBase,
      entityName: 'community-lod-' + this.config.taskId,
      cameraComponent: this.camera?.camera,
      cameraControls: this.controls,
      initialCameraPose: initialPose
        ? { focus: initialPose.focus, cameraPosition: initialPose.position }
        : null
    })
    if (this.destroyed) {
      destroyRemotePlayCanvasLod(session)
      return
    }

    this.lodSession = session
    this.asset = session.asset
    this.model = session.entity
    // LOD already carries an aggregate AABB in its manifest resource. Read it
    // directly and never enter the full-model bounds calculation path.
    this.bounds = readLodBounds(session.asset)
    // Re-apply after the manifest AABB is available so bg_remove follows the
    // same focus-selection rule as a regular SOG/PLY model.
    await this.applyInitialPose()
    this.setupAnnotationManager()
    this.resize()
  }

  async loadModel(buffer, format) {
    this.clearModel()
    const asset = await loadGsplat(this.app.assets, {
      filename: `${this.meta?.title || 'model'}.${format}`,
      contents: buffer,
      minimalMemory: /iPhone|iPad|iPod/i.test(navigator.userAgent)
    })
    if (this.destroyed) {
      this.app.assets.remove(asset)
      asset.unload?.()
      return
    }
    const entity = new pc.Entity('community-gsplat')
    entity.addComponent('gsplat', { asset, unified: false })
    this.app.root.addChild(entity)
    this.asset = asset
    this.model = entity
    this.bounds = computeFullModelBounds(entity, asset)
    await this.applyInitialPose()
    this.setupAnnotationManager()
    this.resize()
  }

  async load() {
    if (this.loading || this.destroyed) return
    const { taskId, testModelUrl } = this.config
    if (!taskId && !testModelUrl) {
      const error = new Error('缺少 taskId，无法定位社区模型')
      error.statusCode = 404
      this.setError(error)
      return
    }

    this.loading = true
    this.loaded = false
    this.stopCameraTrajectory()
    this.cameraTrajectory = []
    this.emit('onCameraTrajectory', { available: false, playing: false })
    if (this.videoRecording) await this.cancelVideoRecording({ silent: true })
    this.clearRecordedVideo()
    this.emitVideoRecordingState({ available: false, mode: '', status: 'idle' })
    this.readyEmitted = false
    window.clearTimeout(this.readyTimer)
    this.emit('onLoaded', false)
    if (this.controls?.autoRotate) this.controls.autoRotate = false
    this.setLoading('正在加载模型', '', null, true, null, { showProgressDetails: false })
    let deferredAnnotations = null

    try {
      await this.createViewer()
      if (this.destroyed) return
      if (testModelUrl) {
        const response = await fetch(testModelUrl)
        if (!response.ok) throw new Error(`测试模型读取失败 (${response.status})`)
        this.meta = {
          taskId: 'local-test',
          title: '本地测试模型',
          description: '本地开发模型',
          author: 'MeTop 用户',
          avatar: '',
          likes: 0,
          isLiked: false,
          views: 0,
          createdAt: '',
          pipeline: '3dgs',
          modelFormat: 'sog'
        }
        this.emit('onMeta', this.meta)
        this.setting = {}
        this.cameraData = null
        await this.loadModel(await response.arrayBuffer(), 'sog')
      } else {
        const task = await this.api.getTask(taskId, this.config.activity, this.config.shareId)
        if (!task || !Object.keys(task).length) {
          const error = new Error('这个空间不存在或已删除')
          error.statusCode = 404
          throw error
        }
        this.setLoading('正在加载模型', '', null, true, null, { showProgressDetails: false })
        const [setting, cameraData, annotations] = await Promise.all([
          this.api.getSetting(taskId, this.config.activity, this.config.shareId).catch(error => {
            console.warn('[MeTop viewer] model settings unavailable', error)
            return {}
          }),
          this.api.getCameraData(taskId, this.config.activity, this.config.shareId).catch(error => {
            console.warn('[MeTop viewer] cameraData unavailable', error)
            return null
          }),
          this.api.getAnnotations(taskId, this.config.activity).catch(error => {
            console.warn('[MeTop viewer] annotations unavailable', error)
            return []
          })
        ])
        this.setting = setting || {}
        this.cameraData = cameraData
        this.task = mergeTaskModelMetadata(task, this.setting, {
          pipeline_type: this.config.pipelineHint,
          model_format: this.config.requestedFormat
        })
        this.cameraTrajectory = readCameraDataTrajectory(this.cameraData)
        this.emit('onCameraTrajectory', {
          available: this.cameraTrajectory.length >= 2,
          playing: false
        })
        this.emitVideoRecordingState()
        this.meta = mapTask(this.task, this.config.fallbackTitle)
        this.emit('onMeta', this.meta)
        document.title = `${this.meta.title} · MeTop.ai`
        const formatCandidates = resolveModelFormatCandidates(
          this.task,
          this.config.requestedFormat,
          this.config.pipelineHint
        )
        let format = formatCandidates[0] || 'sog'
        this.setLoading('正在加载模型', '', null, true, null, { showProgressDetails: false })
        let modelLoaded = false
        let lastModelError = null
        for (let index = 0; index < formatCandidates.length; index += 1) {
          const candidate = formatCandidates[index]
          let modelDownloaded = false
          try {
            this.format = candidate
            if (candidate === 'tar') {
              const manifestUrl = this.task.lod_manifest_url || this.task.lodManifestUrl
              if (!manifestUrl) throw new Error('PlayCanvas LOD manifest URL is missing')
              await this.loadLodModel(manifestUrl)
            } else {
              const buffer = await this.api.downloadModel(taskId, candidate, progress => {
                if (progress.requesting) {
                  this.setLoading('正在加载模型', '', null, true, progress)
                  return
                }
                if (Number.isFinite(progress.percent)) {
                  this.setLoading('正在加载模型', '', progress.percent, false, progress)
                  return
                }
                this.setLoading('正在加载模型', '', null, true, progress)
              }, this.config.activity, this.config.shareId)
              modelDownloaded = true
              await this.loadModel(buffer, candidate)
            }
            format = candidate
            modelLoaded = true
            break
          } catch (error) {
            lastModelError = error
            const status = Number(error?.statusCode || error?.status || 0)
            const hasFallback = index < formatCandidates.length - 1
            const canFallback = modelDownloaded || [404, 415, 422].includes(status)
            if (!hasFallback || !canFallback || this.destroyed) throw error
            console.warn(`[MeTop viewer] ${candidate.toUpperCase()} unavailable, trying ${formatCandidates[index + 1].toUpperCase()}`, error)
            this.clearModel()
            this.setLoading('正在加载模型', '', null, true, null, { showProgressDetails: false })
          }
        }
        if (!modelLoaded) throw lastModelError || new Error('模型文件加载失败')
        if (format === 'tar') deferredAnnotations = annotations
        else this.loadAnnotations(annotations)
      }

      if (this.destroyed) return
      this.loaded = true
      this.emitVideoRecordingState()
      if (taskId && !testModelUrl && this.config.authToken) void this.loadModelImageVideo()
      let entranceReady = false
      this.emit('onEntrance', true)
      const entrancePlayed = await this.entrance?.play({
        entity: this.model,
        bounds: this.bounds,
        pose: this.initialPose,
        onReady: () => {
          entranceReady = true
          this.finishLoading()
        }
      })
      if (!entrancePlayed && !entranceReady) this.finishLoading()
      if (deferredAnnotations && !this.destroyed) this.loadAnnotations(deferredAnnotations)
      this.emit('onToast', '单指旋转 · 双指缩放/平移')
    } catch (error) {
      console.error('[MeTop viewer] load failed', error)
      this.setError(error)
    } finally {
      this.loading = false
      this.emit('onEntrance', false)
      if (this.controls) this.controls.enabled = !this.tourActive
    }
  }

  resetView({ toast = true } = {}) {
    if (!this.controls || !this.initialPose) return false
    this.controls.autoRotate = false
    this.controls.reset(this.initialPose.focus, this.initialPose.position, { duration: 0.5 })
    if (toast) this.emit('onToast', '视角已复位')
    return true
  }

  async captureInitialViewForVideo() {
    if (!this.loaded || !this.app || !this.controls || !this.canvas || !this.initialPose) {
      throw new Error('模型初始视角尚未准备好')
    }
    const saved = {
      pose: this.controls.getPose(),
      mode: this.controls.mode,
      enabled: this.controls.enabled,
      autoRotate: this.controls.autoRotate
    }
    this.stopCameraTrajectory()
    try {
      this.controls.transition = null
      this.controls.autoRotate = false
      this.controls.enabled = false
      if (this.controls.mode !== 'orbit') this.controls.setMode('orbit')
      this.controls.setPose(this.initialPose.focus, this.initialPose.position, { immediate: true, constrain: false })
      this.app.render()
      const canvas = copyCanvasForUpload(this.canvas, 1440)
      const safeTitle = String(this.meta?.title || 'metop-model')
        .replace(/[^\p{L}\p{N}._-]+/gu, '-')
        .replace(/^-+|-+$/g, '')
        .slice(0, 48) || 'metop-model'
      return await encodeJpeg(canvas, `${safeTitle}-initial.jpg`, 0.86)
    } finally {
      this.controls.transition = null
      this.controls.setPose(saved.pose.focus, saved.pose.position, { immediate: true, constrain: false })
      if (saved.mode !== this.controls.mode) this.controls.setMode(saved.mode)
      this.controls.enabled = saved.enabled && !this.tourActive
      this.controls.clearTransientInput()
      if (saved.autoRotate && saved.mode === 'orbit') this.controls.autoRotate = true
      this.app.render()
    }
  }

  async waitForImageVideoPoll(signal) {
    await new Promise((resolve, reject) => {
      let timer = 0
      const abort = () => {
        window.clearTimeout(timer)
        reject(new DOMException('Aborted', 'AbortError'))
      }
      if (signal.aborted) abort()
      else {
        signal.addEventListener('abort', abort, { once: true })
        timer = window.setTimeout(() => {
          signal.removeEventListener('abort', abort)
          resolve()
        }, 6000)
      }
    })
  }

  loadModelImageVideo() {
    if (this.imageVideoLookupPromise) return this.imageVideoLookupPromise
    if (!this.config.authToken || !this.config.taskId || this.destroyed) return Promise.resolve(false)
    const controller = new AbortController()
    this.imageVideoLookupController = controller
    this.imageVideoLookupState = 'checking'
    this.imageVideoIssue = null
    const lookup = (async () => {
      try {
        await this.ensureImageVideoVersion(controller.signal)
        const task = await this.api.getImageVideoTaskByModel(this.config.taskId, { signal: controller.signal })
        if (controller.signal.aborted || this.destroyed) return false
        if (!task) {
          this.imageVideoLookupState = 'missing'
          this.pendingImageVideoTaskId = ''
          this.generatedImageVideo = null
          this.imageVideoPanelOpen = false
          this.emitImageVideoState({ status: 'idle' })
          return false
        }
        this.imageVideoLookupState = 'found'
        const taskId = String(task.task_id || task.taskId || '').trim()
        if (String(task.status || '').toLowerCase() === 'failed') {
          this.pendingImageVideoTaskId = ''
          this.generatedImageVideo = null
          const taskError = task.error && typeof task.error === 'object' ? task.error : {}
          const retryable = taskError.retryable !== false
          this.imageVideoIssue = {
            message: imageVideoFailureMessage({
              code: taskError.code || task.error_code || task.code || '',
              payload: taskError,
              message: taskError.message
            }, '视频生成失败'),
            retryable,
            resumeOnly: false
          }
          this.imageVideoLookupState = 'failed'
          this.emitImageVideoState()
          return false
        }
        if (!taskId) throw new Error('模型视频查询未返回任务 ID')
        this.pendingImageVideoTaskId = taskId
        const generation = {
          controller: new AbortController(), taskId,
          status: 'queued', stageText: '正在查询视频任务'
        }
        this.generatedImageVideo = null
        this.imageVideoGeneration = generation
        void this.continueImageVideoTask(generation, task)
        return true
      } catch (error) {
        if (controller.signal.aborted || this.destroyed) return false
        console.error('[MeTop viewer] model image video lookup failed', error)
        this.imageVideoLookupState = 'error'
        this.imageVideoIssue = {
          message: getServicePausedMessage(error) || error?.message || '视频任务查询失败，请重试',
          resumeOnly: true
        }
        this.emitImageVideoState()
        return false
      } finally {
        if (this.imageVideoLookupController === controller) this.imageVideoLookupController = null
      }
    })()
    this.imageVideoLookupPromise = lookup
    void lookup.finally(() => {
      if (this.imageVideoLookupPromise === lookup) this.imageVideoLookupPromise = null
    })
    return lookup
  }

  async ensureImageVideoVersion(signal) {
    if (this.config.channel !== 'miniapp') return ''
    if (this.imageVideoVersionPromise) return this.imageVideoVersionPromise
    this.imageVideoVersionPromise = this.api.getImageVideoAnalysisVersion({ signal })
      .then(versionInfo => {
        this.imageVideoVersion = versionInfo.version
        this.imageVideoVersionRevision = versionInfo.revision
        return this.imageVideoVersion
      })
      .catch(error => {
        // Version lookup is a compatibility guard. Existing task playback can
        // continue when the public version endpoint is temporarily unavailable.
        if (error?.code !== 'MINIAPP_TOKEN_REQUIRED') {
          console.warn('[MeTop viewer] image video version lookup failed', error)
        }
        return ''
      })
      .finally(() => {
        this.imageVideoVersionPromise = null
      })
    return this.imageVideoVersionPromise
  }

  async startImageVideoGeneration() {
    if (!this.config.authToken) {
      this.emit('onAuthRequired', { kind: 'imageVideo' })
      return false
    }
    if (!this.loaded) {
      this.emit('onToast', '模型仍在加载')
      return false
    }
    this.imageVideoPanelOpen = true
    if (this.imageVideoGeneration) {
      this.emitImageVideoState()
      return true
    }
    if (this.imageVideoIssue?.retryable === false) {
      this.emitImageVideoState({ status: 'error', stageText: '生成失败' })
      return false
    }
    const lookupInProgress = Boolean(this.imageVideoLookupPromise)
    if (lookupInProgress) this.emitImageVideoState({ status: 'checking', stageText: '正在查看视频进度' })
    if (lookupInProgress) await this.imageVideoLookupPromise
    if (this.imageVideoGeneration) return true
    if (this.imageVideoIssue?.retryable === false) {
      this.emitImageVideoState({ status: 'error', stageText: '生成失败' })
      return false
    }
    if (lookupInProgress && this.imageVideoIssue && this.imageVideoLookupState === 'found') return false
    if (this.imageVideoLookupState === 'error') {
      if (lookupInProgress) return false
      this.emitImageVideoState({ status: 'checking', stageText: '正在查看视频进度' })
      await this.loadModelImageVideo()
      return this.imageVideoLookupState === 'found'
    }
    if (this.imageVideoLookupState === 'unknown') {
      this.emitImageVideoState({ status: 'checking', stageText: '正在查看视频进度' })
      await this.loadModelImageVideo()
      if (this.imageVideoLookupState === 'error' || this.imageVideoIssue || this.imageVideoGeneration) return false
    }

    const existing = this.generatedImageVideo
    if (existing?.expiresAt && existing.expiresAt <= Date.now() + 30_000 && this.config.taskId) {
      await this.loadModelImageVideo()
      return this.imageVideoLookupState === 'found'
    }
    if (existing && (!existing.expiresAt || existing.expiresAt > Date.now() + 30_000)) {
      this.emitImageVideoState({ status: 'ready' })
      return true
    }
    if (existing) this.pendingImageVideoTaskId = existing.taskId
    if (this.recordedVideo) this.clearRecordedVideo({ emit: true })
    this.imageVideoIssue = null
    const controller = new AbortController()
    const pendingTaskId = this.pendingImageVideoTaskId
    this.generatedImageVideo = null
    const generation = {
      controller,
      taskId: pendingTaskId,
      status: pendingTaskId ? 'queued' : 'preparing',
      stageText: pendingTaskId ? '正在查询视频任务' : '正在截取初始视角'
    }
    this.imageVideoGeneration = generation
    this.emitImageVideoState()
    try {
      if (!pendingTaskId) {
        const image = await this.captureInitialViewForVideo()
        if (controller.signal.aborted) throw new DOMException('Aborted', 'AbortError')
        Object.assign(generation, { status: 'submitting', stageText: '正在提交图片' })
        this.emitImageVideoState()

        // Creating a task may consume quota, so this POST is deliberately issued once.
        await this.ensureImageVideoVersion(controller.signal)
        const created = await this.api.createImageVideoTask(image, image.name, {
          signal: controller.signal,
          modelTaskId: this.config.taskId,
          generationVersion: this.imageVideoVersion
        })
        generation.taskId = created.task_id
        this.pendingImageVideoTaskId = created.task_id
        return await this.continueImageVideoTask(generation, created.status ? created : null)
      }
      return await this.continueImageVideoTask(generation)
    } catch (error) {
      return this.handleImageVideoTaskError(generation, error)
    }
  }

  async continueImageVideoTask(generation, initialTask = null) {
    const { controller } = generation
    try {
      let nextTask = initialTask
      while (!controller.signal.aborted) {
        const task = nextTask || await this.api.getImageVideoTask(generation.taskId, { signal: controller.signal })
        nextTask = null
        if (controller.signal.aborted || this.imageVideoGeneration !== generation) return false
        const status = String(task.status || '').trim().toLowerCase()
        const stageText = {
          queued: '任务排队中',
          running: '正在生成视频',
          saving: '正在保存视频',
          succeeded: '生成完成',
          failed: '生成失败'
        }[status] || '视频处理中'
        Object.assign(generation, { status: status || 'running', stageText })
        this.emitImageVideoState()

        if (status === 'failed') {
          this.pendingImageVideoTaskId = ''
          this.imageVideoLookupState = 'failed'
          const taskError = task.error && typeof task.error === 'object' ? task.error : {}
          const failure = new Error(taskError.message || task.message || '视频生成失败')
          failure.code = taskError.code || task.error_code || task.code || ''
          failure.payload = taskError
          failure.retryable = taskError.retryable !== false
          throw failure
        }
        if (status === 'succeeded') {
          const video = task.video || task.result?.video || task.result || {}
          const url = String(video.url || video.video_url || task.video_url || '').trim()
          if (!/^https:\/\//i.test(url)) throw new Error('视频生成成功但未返回安全播放地址')
          const rawExpiresAt = video.expires_at ?? video.expiresAt ?? task.expires_at ?? task.expiresAt ?? 0
          const numericExpiresAt = Number(rawExpiresAt)
          const expiresAt = Number.isFinite(numericExpiresAt) && numericExpiresAt > 0
            ? (numericExpiresAt < 1e12 ? numericExpiresAt * 1000 : numericExpiresAt)
            : Date.parse(String(rawExpiresAt || '')) || 0
          this.generatedImageVideo = {
            taskId: generation.taskId,
            url,
            expiresAt,
            filename: String(video.filename || `metop-generated-${generation.taskId}.mp4`)
          }
          this.pendingImageVideoTaskId = ''
          this.imageVideoLookupState = 'found'
          this.imageVideoGeneration = null
          this.imageVideoIssue = null
          this.emitImageVideoState({ status: 'ready', stageText: '生成完成' })
          return true
        }
        await this.waitForImageVideoPoll(controller.signal)
      }
    } catch (error) {
      return this.handleImageVideoTaskError(generation, error)
    }
    return false
  }

  handleImageVideoTaskError(generation, error) {
    const aborted = error?.name === 'AbortError'
    if (!aborted) console.error('[MeTop viewer] image video generation failed', error)
    if (this.imageVideoGeneration !== generation) return false
    this.imageVideoGeneration = null
    if (!aborted && [401, 403, 491, 901].includes(Number(error?.statusCode))) {
      this.imageVideoIssue = null
      this.emitImageVideoState({ status: 'idle' })
      this.emit('onAuthRequired', { kind: 'imageVideo' })
      return false
    }
    if (!aborted && !this.destroyed) {
      const permanent = isPermanentImageVideoFailure(error)
      const retryable = !permanent && error?.retryable !== false
      if (permanent) {
        this.pendingImageVideoTaskId = ''
        this.imageVideoLookupState = 'failed'
      }
      const message = imageVideoFailureMessage(error, generation.taskId
        ? '查询视频任务失败'
        : '创建视频任务失败，提交结果可能未确认')
      this.imageVideoIssue = {
        message,
        retryable,
        resumeOnly: Boolean(this.pendingImageVideoTaskId) && retryable
      }
      this.emitImageVideoState({ status: 'error', stageText: '生成失败' })
    } else if (!this.destroyed) {
      this.emitImageVideoState({ status: 'idle' })
    }
    return false
  }

  closeGeneratedImageVideo() {
    this.imageVideoPanelOpen = false
    this.emitImageVideoState()
  }

  openImageVideoNotification() {
    const miniProgram = window.wx?.miniProgram
    const taskId = String(
      this.imageVideoGeneration?.taskId ||
      this.pendingImageVideoTaskId ||
      this.generatedImageVideo?.taskId ||
      ''
    ).trim()
    if (!taskId) {
      this.emit('onToast', '视频任务尚未创建，请稍后再试')
      return false
    }
    if (!this.config.embedded || !miniProgram?.navigateTo) {
      this.emit('onToast', '请在小程序中开启生成提醒')
      return false
    }
    const query = [
      `taskId=${encodeURIComponent(taskId)}`,
      `modelTaskId=${encodeURIComponent(this.config.taskId || '')}`
    ].join('&')
    miniProgram.navigateTo({
      url: `/pages/video-notify/index?${query}`,
      fail: () => this.emit('onToast', '提醒设置页打开失败，请稍后再试')
    })
    return true
  }

  async openGeneratedImageVideoNativeAction(action) {
    const video = this.generatedImageVideo
    if (!video || this.generatedImageVideoSaving) return false
    if (!this.config.embedded || !window.wx?.miniProgram?.navigateTo) {
      if (video.expiresAt && video.expiresAt <= Date.now()) {
        this.emit('onToast', '视频链接已过期，请重新打开预览')
        return false
      }
      if (action === 'share' && navigator.share) {
        await navigator.share({ title: this.meta?.title || 'MeTop.ai 生成视频', url: video.url })
        return true
      }
      const link = document.createElement('a')
      link.href = video.url
      link.download = video.filename
      link.click()
      return true
    }
    this.generatedImageVideoSaving = true
    this.emitImageVideoState({ status: 'ready', saving: true, savingAction: action })
    try {
      const query = [
        `taskId=${encodeURIComponent(video.taskId)}`,
        `modelTaskId=${encodeURIComponent(this.config.taskId || '')}`,
        `action=${encodeURIComponent(action)}`,
        'source=image-video'
      ].join('&')
      await new Promise((resolve, reject) => window.wx.miniProgram.navigateTo({
        url: `/pages/video-save/index?${query}`,
        success: resolve,
        fail: reject
      }))
      return true
    } catch (error) {
      this.emit('onToast', action === 'share' ? '视频分享页打开失败' : '视频保存页打开失败')
      return false
    } finally {
      this.generatedImageVideoSaving = false
      if (this.generatedImageVideo) this.emitImageVideoState({ status: 'ready', saving: false, savingAction: '' })
    }
  }

  saveGeneratedImageVideo() {
    return this.openGeneratedImageVideoNativeAction('save')
  }

  shareGeneratedImageVideo() {
    return this.openGeneratedImageVideoNativeAction('share')
  }

  toggleCameraTrajectory() {
    if (this.cameraTrajectoryPlayback) {
      this.stopCameraTrajectory()
      return false
    }
    if (!this.loaded || this.cameraTrajectory.length < 2 || !this.controls) return false
    this.controls.autoRotate = false
    this.controls.transition = null
    this.controls.clearTransientInput()
    this.controls.enabled = false
    this.cameraTrajectoryPlayback = { index: 0, elapsed: 0 }
    const first = this.cameraTrajectory[0]
    this.controls.setPose(first.focus, first.position, { immediate: true, constrain: false })
    this.emit('onCameraTrajectory', { available: true, playing: true })
    return true
  }

  stopCameraTrajectory() {
    if (!this.cameraTrajectoryPlayback) return false
    this.cameraTrajectoryPlayback = null
    if (this.controls) {
      this.controls.enabled = !this.tourActive
      this.controls.clearTransientInput()
    }
    this.emit('onCameraTrajectory', {
      available: this.cameraTrajectory.length >= 2,
      playing: false
    })
    return true
  }

  async startVideoRecording() {
    const mode = this.getVideoRecordingMode()
    if (!this.loaded || !this.controls || !mode || this.videoRecording || this.videoRecordingStarting) return false
    if (!supportsCanvasVideoRecording(this.canvas)) {
      this.emit('onToast', '当前浏览器暂不支持视频录制')
      return false
    }

    this.videoRecordingStarting = true
    const savedPose = this.controls.getPose()
    const cameraTrajectoryWasPlaying = Boolean(this.cameraTrajectoryPlayback)
    const saved = {
      pose: savedPose,
      mode: this.controls.mode,
      enabled: this.controls.enabled || cameraTrajectoryWasPlaying,
      autoRotate: this.controls.autoRotate
    }
    this.stopCameraTrajectory()
    this.controls.autoRotate = false
    this.controls.transition = null
    this.controls.clearTransientInput()
    this.controls.enabled = false
    if (this.controls.mode !== 'orbit') this.controls.setMode('orbit')

    const startPose = mode === 'trajectory'
      ? { focus: this.cameraTrajectory[0].focus.clone(), position: this.cameraTrajectory[0].position.clone() }
      : { focus: savedPose.focus.clone(), position: savedPose.position.clone() }
    const duration = mode === 'trajectory'
      ? this.cameraTrajectory.slice(0, -1).reduce((total, item) => total + Math.max(item.duration, 0.001), 0)
      : Math.max(4, Math.min(30, 360 / Math.max(1, Number(this.controls.autoRotateSpeed) || 45)))

    try {
      this.clearRecordedVideo()
      this.controls.setPose(startPose.focus, startPose.position, { immediate: true, constrain: false })
      this.app.renderNextFrame = true
      const recorder = await startCanvasVideoRecording(this.canvas)
      recorder.result.catch(() => {})
      this.videoRecording = {
        mode,
        recorder,
        elapsed: 0,
        duration,
        startPose,
        saved,
        finishing: false,
        cancelled: false,
        cancelSilent: false
      }
      this.emitVideoRecordingState({ status: 'recording', progress: 0, url: '', filename: '' })
      return true
    } catch (error) {
      this.restoreVideoRecordingState({ saved })
      console.error('[MeTop viewer] video recording start failed', error)
      this.emit('onToast', '当前浏览器暂不支持视频录制')
      this.emitVideoRecordingState({ status: 'idle', progress: 0 })
      return false
    } finally {
      this.videoRecordingStarting = false
    }
  }

  restoreVideoRecordingState(recording) {
    const saved = recording?.saved
    if (!saved || !this.controls) return
    this.controls.transition = null
    this.controls.setPose(saved.pose.focus, saved.pose.position, { immediate: true, constrain: false })
    if (saved.mode !== this.controls.mode) this.controls.setMode(saved.mode)
    this.controls.enabled = saved.enabled && !this.tourActive
    this.controls.clearTransientInput()
    if (saved.autoRotate && saved.mode === 'orbit') this.controls.autoRotate = true
    if (this.app) this.app.renderNextFrame = true
  }

  async finishVideoRecording({ cancelled = false, silent = false } = {}) {
    const recording = this.videoRecording
    if (!recording) return false
    if (cancelled) {
      recording.cancelled = true
      recording.cancelSilent = silent
    }
    if (recording.finishing) return false
    recording.finishing = true
    this.emitVideoRecordingState({ status: 'processing', progress: 100 })
    this.restoreVideoRecordingState(recording)

    try {
      const blob = await recording.recorder.stop()
      if (!recording.cancelled && !this.destroyed) {
        const safeTitle = String(this.meta?.title || 'metop-model')
          .replace(/[^\p{L}\p{N}._-]+/gu, '-')
          .replace(/^-+|-+$/g, '')
          .slice(0, 48) || 'metop-model'
        const filename = `${safeTitle}-${Date.now()}.${recording.recorder.extension}`
        this.recordedVideo = { blob, filename }
        this.recordedVideoUrl = URL.createObjectURL(blob)
        this.videoRecording = null
        this.emitVideoRecordingState({ status: 'ready', progress: 100 })
        return true
      }
    } catch (error) {
      if (!recording.cancelled && !this.destroyed) {
        console.error('[MeTop viewer] video recording failed', error)
        this.emit('onToast', '视频生成失败，请重试')
      }
    }

    this.videoRecording = null
    this.emitVideoRecordingState({ status: 'idle', progress: 0, url: '', filename: '' })
    if (recording.cancelled && !recording.cancelSilent) this.emit('onToast', '已取消录制')
    return false
  }

  cancelVideoRecording(options = {}) {
    return this.finishVideoRecording({ ...options, cancelled: true })
  }

  clearRecordedVideo({ emit = false } = {}) {
    if (this.recordedVideoUrl) URL.revokeObjectURL(this.recordedVideoUrl)
    this.recordedVideoUrl = ''
    this.recordedVideo = null
    this.recordedVideoSaving = false
    if (emit) this.emitVideoRecordingState({ status: 'idle', progress: 0, url: '', filename: '', saving: false, saveProgress: 0, savingAction: '' })
  }

  closeRecordedVideo() {
    this.clearRecordedVideo({ emit: true })
  }

  downloadRecordedVideoInBrowser() {
    if (!this.recordedVideo || !this.recordedVideoUrl) return false
    const link = document.createElement('a')
    link.href = this.recordedVideoUrl
    link.download = this.recordedVideo.filename
    document.body.appendChild(link)
    link.click()
    link.remove()
    return true
  }

  async openRecordedVideoNativeAction(action) {
    if (!this.recordedVideo || !this.recordedVideoUrl || this.recordedVideoSaving) return false
    if (!this.config.authToken) {
      this.emit('onToast', action === 'share' ? '请先登录后分享视频' : '请先登录后保存到相册')
      return false
    }
    const miniProgram = window.wx?.miniProgram
    if (!miniProgram?.navigateTo) {
      this.emit('onToast', '当前微信版本暂不支持保存')
      return false
    }

    this.recordedVideoSaving = true
    this.emitVideoRecordingState({ status: 'ready', saving: true, saveProgress: 0, savingAction: action })
    try {
      let uploaded = this.recordedVideo.nativeSave
      if (!uploaded || (uploaded.expiresAt && uploaded.expiresAt <= Date.now() + 60_000)) {
        uploaded = await this.api.uploadRecordedVideo(
          this.config.taskId,
          this.recordedVideo.blob,
          this.recordedVideo.filename,
          {
            onProgress: progress => this.emitVideoRecordingState({
              status: 'ready',
              saving: true,
              saveProgress: progress,
              savingAction: action
            })
          }
        )
        this.recordedVideo.nativeSave = uploaded
      }
      const query = [
        `url=${encodeURIComponent(uploaded.url)}`,
        `filename=${encodeURIComponent(uploaded.filename || this.recordedVideo.filename)}`,
        `action=${encodeURIComponent(action)}`
      ].join('&')
      await new Promise((resolve, reject) => {
        miniProgram.navigateTo({
          url: `/pages/video-save/index?${query}`,
          success: resolve,
          fail: reject
        })
      })
      return true
    } catch (error) {
      console.error('[MeTop viewer] save recorded video failed', error)
      const message = Number(error?.statusCode) === 404
        ? '视频服务尚未部署，请稍后再试'
        : String(error?.message || '').includes('登录')
          ? action === 'share' ? '请先登录后分享视频' : '请先登录后保存到相册'
          : '视频上传失败，请稍后重试'
      this.emit('onToast', message)
      return false
    } finally {
      this.recordedVideoSaving = false
      if (this.recordedVideo) this.emitVideoRecordingState({ status: 'ready', saving: false, saveProgress: 0, savingAction: '' })
    }
  }

  async saveRecordedVideo() {
    if (!this.config.embedded) {
      const downloaded = this.downloadRecordedVideoInBrowser()
      if (downloaded) this.emit('onToast', '视频下载已开始')
      return downloaded
    }
    return this.openRecordedVideoNativeAction('save')
  }

  async shareRecordedVideo() {
    if (!this.recordedVideo) return false
    if (this.config.embedded) return this.openRecordedVideoNativeAction('share')
    try {
      if (typeof File === 'function') {
        const file = new File([this.recordedVideo.blob], this.recordedVideo.filename, { type: this.recordedVideo.blob.type })
        if (navigator.share && (!navigator.canShare || navigator.canShare({ files: [file] }))) {
          await navigator.share({ title: this.meta?.title || 'MeTop.ai 3D 模型', files: [file] })
          return true
        }
      }
      const downloaded = this.downloadRecordedVideoInBrowser()
      if (downloaded) this.emit('onToast', '当前浏览器无法直接分享，视频下载已开始')
      return downloaded
    } catch (error) {
      if (error.name !== 'AbortError') this.emit('onToast', '分享失败，请先保存视频')
    }
    return false
  }

  getCurrentCameraPosePayload() {
    const pose = this.controls?.getPose?.()
    const position = pose?.position
    const focus = pose?.focus
    const values = [
      position?.x, position?.y, position?.z,
      focus?.x, focus?.y, focus?.z
    ].map(Number)
    if (!values.every(Number.isFinite)) return null
    return {
      cameraPosition: { x: values[0], y: values[1], z: values[2] },
      focus: { x: values[3], y: values[4], z: values[5] }
    }
  }

  async saveInitialCameraPose() {
    if (this.pendingInitialCameraSave) return false
    if (this.config.viewMode !== 'project') throw new Error('社区模型为只读模式，不能修改初始视角')
    if (!this.config.taskId) throw new Error('当前模型没有任务 ID，无法保存初始视角')
    if (!this.loaded || !this.controls || !this.camera?.camera) throw new Error('模型仍在加载，请稍后再试')

    this.controls.autoRotate = false
    const initialCameraPose = this.getCurrentCameraPosePayload()
    if (!initialCameraPose) throw new Error('当前视角无效，请调整后重试')

    const setting = {
      ...this.setting,
      moveSpeed: Number(this.controls.moveSpeed),
      orbitSpeed: Number(this.controls.orbitSpeed),
      autoRotateSpeed: Number(this.controls.autoRotateSpeed),
      pinchSpeed: Number(this.controls.pinchSpeed),
      originalFov: Number(this.camera.camera.fov),
      backgroundColor: this.background,
      orbitMotionType: this.orbitMotionType,
      motionData: this.motionData,
      initialCameraPose,
      skyboxEnabled: this.skyboxEnabled,
      skyboxPreset: this.skyboxPreset
    }

    this.pendingInitialCameraSave = true
    try {
      await this.api.saveSetting(this.config.taskId, setting, this.config.activity)
      this.setting = setting
      this.initialPose = {
        position: new pc.Vec3(
          initialCameraPose.cameraPosition.x,
          initialCameraPose.cameraPosition.y,
          initialCameraPose.cameraPosition.z
        ),
        focus: new pc.Vec3(
          initialCameraPose.focus.x,
          initialCameraPose.focus.y,
          initialCameraPose.focus.z
        )
      }
      this.emit('onSettings', { initialCameraPose })
      return initialCameraPose
    } finally {
      this.pendingInitialCameraSave = false
    }
  }

  setControlMode(mode) {
    if (!this.controls) return 'orbit'
    const next = this.controls.setMode(mode)
    if (next === 'fly' && this.annotationEditMode) this.setAnnotationEditMode(false)
    if (next === 'fly') {
      this.surfaceRing = clearSurfaceRingMarker(this.surfaceRing)
      this.pointerDown = null
      this.tap = null
      this.nativeTouchTap = null
      this.activePointers.clear()
      window.clearTimeout(this.tapTimer)
    }
    return next
  }

  toggleCameraMode() {
    const next = this.setControlMode(this.controls?.mode === 'fly' ? 'orbit' : 'fly')
    this.emit('onToast', next === 'fly' ? '已切换到飞行模式' : '已切换到轨道模式')
    return next
  }

  setJoystickInput(input = {}) {
    this.controls?.setJoystickInput(input.left, input.right)
  }

  setSoftwareLandscape(active) {
    this.softwareLandscape = Boolean(active)
    this.controls?.setSoftwareLandscape(this.softwareLandscape)
  }

  setBackground(hex, toast = true) {
    if (!this.camera?.camera) return false
    this.background = /^#[\da-f]{6}$/i.test(String(hex || ''))
      ? String(hex)
      : RENDER_TASK_DEFAULT_SETTINGS.backgroundColor
    this.camera.camera.clearColor = colorFromHex(this.background)
    if (toast) this.emit('onToast', this.skyboxEnabled ? '背景颜色已保存' : '背景颜色已切换')
    return true
  }

  setSkyboxEnabled(enabled, toast = true) {
    this.skyboxEnabled = Boolean(enabled)
    if (this.skyboxEnabled) this.loadSkyboxPreset(this.skyboxPreset)
    this.applySkybox()
    if (toast) this.emit('onToast', this.skyboxEnabled ? '天空盒已开启' : '已切换为背景颜色')
    return this.skyboxEnabled
  }

  setSkyboxPreset(presetId, toast = true) {
    const preset = getSkyboxPreset(presetId)
    if (!preset) return false
    this.skyboxPreset = preset.id
    this.skyboxEnabled = true
    this.loadSkyboxPreset(preset.id)
    this.applySkybox()
    if (toast) this.emit('onToast', `正在切换至${preset.label}`)
    return true
  }

  loadSkyboxPreset(presetId) {
    const preset = getSkyboxPreset(presetId)
    if (!this.app || !preset || this.skyboxTextures.has(preset.id) || this.skyboxLoading.has(preset.id)) return

    const pending = new Promise(resolve => {
      const url = new URL(preset.url, document.baseURI).toString()
      this.app.assets.loadFromUrlAndFilename(url, preset.filename, 'texture', (error, asset) => {
        this.skyboxLoading.delete(preset.id)
        if (this.destroyed || error || !asset?.resource) {
          if (!this.destroyed) {
            console.warn(`[MeTop viewer] failed to load skybox: ${preset.id}`, error)
            this.emit('onToast', '天空盒加载失败')
          }
          resolve(false)
          return
        }

        const source = asset.resource
        let skybox = source
        let generated = false
        if (preset.sourceType === 'equirect') {
          source.projection = pc.TEXTUREPROJECTION_EQUIRECT
          skybox = pc.EnvLighting.generateSkyboxCubemap(source)
          generated = true
        }
        this.skyboxTextures.set(preset.id, { asset, source, skybox, generated })
        if (this.skyboxPreset === preset.id) this.applySkybox()
        resolve(true)
      })
    })
    this.skyboxLoading.set(preset.id, pending)
  }

  applySkybox() {
    if (!this.app) return
    const textures = this.skyboxTextures.get(this.skyboxPreset)
    this.app.scene.skybox = this.skyboxEnabled && textures ? textures.skybox : null
  }

  clearSkyboxes() {
    if (this.app) this.app.scene.skybox = null
    this.skyboxTextures.forEach(({ asset, source, skybox, generated }) => {
      if (asset && this.app) this.app.assets.remove(asset)
      if (generated) skybox?.destroy?.()
      source?.destroy?.()
    })
    this.skyboxTextures.clear()
    this.skyboxLoading.clear()
  }

  setFov(value) {
    if (this.camera?.camera) this.camera.camera.fov = pc.math.clamp(Number(value) || 75, 30, 150)
  }

  setOrbitSpeed(value) {
    if (this.controls) this.controls.orbitSpeed = pc.math.clamp(Number(value) || 24, 1, 60)
  }

  setMoveSpeed(value) {
    if (!this.controls) return
    this.controls.moveSpeed = readRenderTaskNumber(
      value,
      RENDER_TASK_DEFAULT_SETTINGS.moveSpeed,
      RENDER_TASK_SETTING_RANGES.moveSpeed
    )
  }

  setPinchSpeed(value) {
    if (!this.controls) return
    this.controls.pinchSpeed = readRenderTaskNumber(
      value,
      RENDER_TASK_DEFAULT_SETTINGS.pinchSpeed,
      RENDER_TASK_SETTING_RANGES.pinchSpeed
    )
  }

  setTourActive(active) {
    this.tourActive = Boolean(active)
    if (!this.controls) return
    this.controls.enabled = !this.tourActive && !this.entrance?.playing && !this.cameraTrajectoryPlayback
    if (this.tourActive) this.controls.autoRotate = false
  }

  toggleAutoRotate() {
    if (!this.controls) return false
    if (this.controls.mode === 'fly') return false
    this.controls.autoRotate = !this.controls.autoRotate
    this.emit('onToast', this.controls.autoRotate ? '自动旋转已开启' : '自动旋转已关闭')
    return this.controls.autoRotate
  }

  async toggleLike() {
    if (!this.meta) return
    if (!this.config.authToken) {
      this.emit('onAuthRequired', { kind: 'like' })
      return
    }
    const previousLiked = this.meta.isLiked
    const previousCount = this.meta.likes
    this.meta = {
      ...this.meta,
      isLiked: !previousLiked,
      likes: Math.max(0, previousCount + (previousLiked ? -1 : 1))
    }
    this.emit('onMeta', this.meta)
    try {
      await this.api.like(this.config.taskId)
      this.emit('onToast', this.meta.isLiked ? '已点赞' : '已取消点赞')
    } catch (error) {
      this.meta = { ...this.meta, isLiked: previousLiked, likes: previousCount }
      this.emit('onMeta', this.meta)
      if ([401, 403, 491, 901].includes(Number(error.statusCode))) this.emit('onAuthRequired', { kind: 'like' })
      else this.emit('onToast', error.message || '点赞失败')
    }
  }

  setQuickView(view) {
    if (!this.controls || !this.bounds) return false
    this.controls.autoRotate = false
    const directions = {
      front: new pc.Vec3(0, 0, 1),
      right: new pc.Vec3(1, 0, 0),
      top: new pc.Vec3(0.001, 1, 0.001),
      iso: new pc.Vec3(0.7, 0.48, 1)
    }
    this.controls.lookFrom(directions[view] || directions.iso, this.controls.distance, { duration: 0.35 })
    this.emit('onToast', '视角已切换')
    return true
  }

  async focusFromClientPoint(clientX, clientY) {
    if (!this.loaded || this.controls?.mode === 'fly' || this.entrance?.playing || this.focusPending || !this.picker || !this.controls || !this.camera) return false
    const rect = this.canvas.getBoundingClientRect()
    if (!rect.width || !rect.height) return false
    const bufferX = Math.floor(this.softwareLandscape
      ? (clientY - rect.top) * (this.canvas.width / rect.height)
      : (clientX - rect.left) * (this.canvas.width / rect.width))
    const bufferY = Math.floor(this.softwareLandscape
      ? (rect.right - clientX) * (this.canvas.height / rect.width)
      : (clientY - rect.top) * (this.canvas.height / rect.height))
    this.focusPending = true
    try {
      const isLod = Boolean(this.lodSession)
      const point = await this.picker.pick(
        bufferX,
        bufferY,
        this.canvas.width,
        this.canvas.height,
        null,
        undefined,
        { depth: isLod }
      )
      if (!point) return false
      this.surfaceRing = await showSurfaceRingMarker({
        app: this.app,
        marker: this.surfaceRing,
        picker: isLod ? null : this.picker,
        hitPoint: point,
        screenX: bufferX,
        screenY: bufferY,
        width: this.canvas.width,
        height: this.canvas.height,
        cameraEntity: this.camera,
        modelEntity: this.model
      })
      this.controls.autoRotate = false
      this.controls.reset(point, this.camera.getPosition(), { duration: 0.35 })
      return true
    } catch (error) {
      console.warn('[MeTop viewer] focus pick failed', error)
      return false
    } finally {
      this.focusPending = false
    }
  }

  setupAnnotationManager() {
    if (!this.app || !this.model || this.annotationManager) return
    const managerEntity = this.lodSession
      ? new pc.Entity('lod-annotation-manager', this.app)
      : this.model
    if (this.lodSession) {
      this.app.root.addChild(managerEntity)
      this.annotationManagerEntity = managerEntity
    }
    if (!managerEntity.script) managerEntity.addComponent('script')
    this.annotationManager = managerEntity.script?.create(AnnotationManager) || null
    if (!this.annotationManager && this.annotationManagerEntity) {
      this.annotationManagerEntity.destroy()
      this.annotationManagerEntity = null
    }
    if (!this.annotationManager) console.warn('[MeTop viewer] annotation manager unavailable')
    else this.annotationManager.setCalloutMode?.(this.annotationCallout)
  }

  destroyAnnotationManager() {
    if (this.annotationManagerEntity) {
      this.annotationManagerEntity.destroy?.()
      this.annotationManagerEntity = null
      this.annotationManager = null
      return
    }
    if (this.annotationManager?.entity?.script?.annotationManager) {
      this.annotationManager.entity.script.destroy('annotationManager')
    }
    this.annotationManager = null
  }

  createAnnotationEntity(data, { fromServer = false } = {}) {
    if (!this.app || !this.annotationManager) return null
    const position = normalizeVector([data?.x, data?.y, data?.z])
    if (!position) return null
    const label = String(data?.indexLabel || data?.index || this.getNextAnnotationLabel())
    const entity = new pc.Entity(`annotation_${label}_${Date.now()}`, this.app)
    entity.setPosition(position)
    entity.__fromServer = fromServer
    this.app.root.addChild(entity)
    entity.addComponent('script')
    const annotation = entity.script.create(Annotation, {
      properties: {
        label,
        title: String(data?.title ?? data?.label ?? ''),
        text: String(data?.description ?? data?.text ?? ''),
        titleColor: normalizeAnnotationTextColor(data?.titleColor || data?.title_color || data?.color),
        textColor: normalizeAnnotationTextColor(data?.textColor || data?.text_color || data?.color),
        textSize: normalizeAnnotationTextSize(data?.textSize || data?.text_size || data?.fontSize),
        attachments: normalizeAnnotationAttachments(data?.attachments || data?.attachment || data?.files || data?.media),
        size: Math.max(0.01, Number(data?.size) || 1),
        cameraPose: normalizeAnnotationCameraPose(data?.cameraPose || data?.camera_pose || data?.camera || data?.pose)
      }
    })
    this.annotationEntities.push(entity)
    entity.enabled = this.annotationVisible
    return { entity, annotation }
  }

  loadAnnotations(data) {
    this.clearAnnotations()
    ;(Array.isArray(data) ? data : []).forEach((item, index) => {
      this.createAnnotationEntity({
        ...item,
        indexLabel: String(index + 1),
        title: item?.label,
        text: item?.description
      }, { fromServer: true })
    })
    this.annotationBaseline = this.serializeAnnotations()
    this.emitAnnotationState()
  }

  clearAnnotations() {
    this.annotationMenuEntity = null
    const entities = [...this.annotationEntities]
    this.annotationEntities = []
    entities.forEach(entity => {
      revokeLocalAnnotationAttachments(entity?.script?.annotation?.attachments)
      entity?.destroy?.()
    })
    this.emitAnnotationState()
  }

  getAnnotations() {
    return this.annotationEntities
      .map((entity, index) => {
        const annotation = entity?.script?.annotation
        const position = entity?.getPosition?.()
        if (!annotation || !position) return null
        return {
          id: entity.getGuid?.() || entity.name,
          index: index + 1,
          label: String(annotation.label || index + 1),
          title: String(annotation.title || ''),
          text: String(annotation.text || ''),
          titleColor: normalizeAnnotationTextColor(annotation.titleColor),
          textColor: normalizeAnnotationTextColor(annotation.textColor),
          textSize: normalizeAnnotationTextSize(annotation.textSize),
          attachments: normalizeAnnotationAttachments(annotation.attachments),
          size: Number(annotation.size) || 1,
          cameraPose: normalizeAnnotationCameraPose(annotation.cameraPose),
          position: { x: position.x, y: position.y, z: position.z },
          entity
        }
      })
      .filter(Boolean)
  }

  serializeAnnotations() {
    const normalized = this.getAnnotations().map(item => ({
      label: item.label,
      title: item.title,
      text: item.text,
      titleColor: item.titleColor,
      textColor: item.textColor,
      textSize: item.textSize,
      attachments: item.attachments.map(attachment => annotationAttachmentForServer(attachment)),
      size: item.size,
      cameraPose: item.cameraPose,
      x: Number(item.position.x.toFixed(6)),
      y: Number(item.position.y.toFixed(6)),
      z: Number(item.position.z.toFixed(6))
    })).sort((left, right) => String(left.label).localeCompare(String(right.label), undefined, { numeric: true }))
    return JSON.stringify(normalized)
  }

  emitAnnotationState(extra = {}) {
    const annotations = this.getAnnotations().map(({ entity, ...item }) => item)
    this.emit('onAnnotations', {
      annotations,
      visible: this.annotationVisible,
      expanded: this.annotationExpanded,
      callout: this.annotationCallout,
      editing: this.annotationEditMode,
      changed: this.serializeAnnotations() !== this.annotationBaseline,
      ...extra
    })
  }

  setAnnotationEditMode(active) {
    const next = Boolean(active)
    if (next && this.annotationReadOnly) return false
    if (!this.controls || (next && (!this.loaded || this.controls.mode === 'fly'))) return false
    if (next && !this.annotationEditMode) {
      this.annotationEditSnapshot = this.getAnnotations().map(item => ({
        label: item.label,
        title: item.title,
        text: item.text,
        titleColor: item.titleColor,
        textColor: item.textColor,
        textSize: item.textSize,
        attachments: item.attachments.map(attachment => ({ ...attachment })),
        size: item.size,
        cameraPose: item.cameraPose,
        x: item.position.x,
        y: item.position.y,
        z: item.position.z,
        __fromServer: Boolean(item.entity?.__fromServer)
      }))
    }
    this.annotationEditMode = next
    if (!next) this.annotationMenuEntity = null
    this.controls.autoRotate = false
    this.annotationVisible = true
    this.annotationEntities.forEach(entity => { entity.enabled = true })
    this.app?.fire('annotation:editMode', this.annotationEditMode)
    this.annotationExpanded = false
    this.annotationManager?.hideAllTooltips?.()
    this.emitAnnotationState()
    return this.annotationEditMode
  }

  discardAnnotationChanges() {
    if (!Array.isArray(this.annotationEditSnapshot)) return false
    const preservedUrls = new Set(this.annotationEditSnapshot.flatMap(item => (
      normalizeAnnotationAttachments(item.attachments).map(attachment => attachment.url)
    )))
    const entities = [...this.annotationEntities]
    this.annotationEntities = []
    entities.forEach(entity => {
      normalizeAnnotationAttachments(entity?.script?.annotation?.attachments).forEach(attachment => {
        if (String(attachment.url || '').startsWith('blob:') && !preservedUrls.has(attachment.url)) {
          URL.revokeObjectURL(attachment.url)
        }
      })
      entity?.destroy?.()
    })
    this.annotationEditSnapshot.forEach(item => {
      this.createAnnotationEntity({ ...item, indexLabel: item.label }, { fromServer: Boolean(item.__fromServer) })
    })
    this.annotationEditSnapshot = null
    this.emitAnnotationState()
    return true
  }

  toggleAnnotationVisibility() {
    this.annotationVisible = !this.annotationVisible
    this.annotationEntities.forEach(entity => { entity.enabled = this.annotationVisible })
    if (!this.annotationVisible) {
      this.annotationExpanded = false
      this.annotationManager?.hideAllTooltips?.()
    }
    this.emitAnnotationState()
    return this.annotationVisible
  }

  toggleAllAnnotationPanels(expanded) {
    if (!this.annotationManager) return false
    this.annotationExpanded = Boolean(expanded)
    if (this.annotationExpanded) {
      this.annotationVisible = true
      this.annotationEntities.forEach(entity => { entity.enabled = true })
      this.annotationManager.showAllTooltips?.()
    } else {
      this.annotationManager.hideAllTooltips?.()
    }
    this.emitAnnotationState()
    return this.annotationExpanded
  }

  toggleAnnotationStyle(callout) {
    if (!this.annotationManager) return false
    this.annotationCallout = Boolean(callout)
    this.annotationManager.setCalloutMode?.(this.annotationCallout)
    // Presentation and expansion are independent controls. Switching between
    // numbered hotspots and callouts must not open every annotation panel.
    this.emitAnnotationState()
    return this.annotationCallout
  }

  findAnnotationEntity(id) {
    return this.annotationEntities.find(entity => (
      entity.getGuid?.() === id || entity.name === id || entity.script?.annotation?.label === String(id)
    )) || null
  }

  annotationLocalPointCandidates(clientX, clientY) {
    const x = Number(clientX)
    const y = Number(clientY)
    if (!Number.isFinite(x) || !Number.isFinite(y)) return []
    const parent = this.annotationManager?._parentDom || this.root
    const rect = parent?.getBoundingClientRect?.()
    const width = Number(parent?.clientWidth) || Number(rect?.width)
    const height = Number(parent?.clientHeight) || Number(rect?.height)
    if (!rect?.width || !rect?.height || !width || !height) return []

    const candidates = []
    const add = point => {
      if (!Number.isFinite(point.x) || !Number.isFinite(point.y)) return
      if (candidates.some(item => Math.hypot(item.x - point.x, item.y - point.y) < 0.5)) return
      candidates.push(point)
    }

    if (this.softwareLandscape && parent === this.root) {
      // The viewer is rotated clockwise by CSS. Touch coordinates stay in the
      // portrait viewport, while hotspot left/top values stay in the unrotated
      // landscape coordinate system.
      add({
        x: (y - rect.top) * (width / rect.height),
        y: height - (x - rect.left) * (height / rect.width)
      })

      // Older WKWebView / XWeb releases have reported touch coordinates in the
      // element's pre-transform space. These lower-priority candidates keep
      // those engines usable without weakening the normal hit path.
      add({ x, y })
      add({
        x: (x - rect.left) * (width / rect.width),
        y: (y - rect.top) * (height / rect.height)
      })
    } else {
      add({
        x: (x - rect.left) * (width / rect.width),
        y: (y - rect.top) * (height / rect.height)
      })
    }
    return candidates
  }

  findAnnotationAtLocalPoint(x, y) {
    let closest = null
    let closestDistance = Infinity
    for (const [annotation, resources] of this.annotationManager?._annotationResources || []) {
      if (!annotation || annotation.label === '0' || !annotation.entity?.enabled) continue
      const hotspot = resources?.hotspotDom
      if (!hotspot || hotspot.style.display === 'none') continue

      // left/top are the hotspot center in the viewer's untransformed logical
      // coordinate space. They remain reliable even when a native WebView gets
      // the transformed DOM hit box wrong.
      const centerX = Number.parseFloat(hotspot.style.left)
      const centerY = Number.parseFloat(hotspot.style.top)
      if (!Number.isFinite(centerX) || !Number.isFinite(centerY)) continue
      const width = hotspot.offsetWidth || Number.parseFloat(hotspot.style.width) || 30
      const height = hotspot.offsetHeight || Number.parseFloat(hotspot.style.height) || 30
      const hitRadius = Math.max(15, width / 2, height / 2) + 16
      const distance = Math.hypot(x - centerX, y - centerY)
      if (distance <= hitRadius && distance < closestDistance) {
        closest = annotation
        closestDistance = distance
      }
    }
    return closest
  }

  findAnnotationAtClientPoint(clientX, clientY) {
    if (!this.annotationVisible || !this.annotationManager) return null
    for (const point of this.annotationLocalPointCandidates(clientX, clientY)) {
      const annotation = this.findAnnotationAtLocalPoint(point.x, point.y)
      if (annotation) return annotation
    }
    return null
  }

  rememberAnnotationActivation(annotation, clientX, clientY, time = performance.now(), source = 'unknown') {
    this.lastAnnotationActivation = {
      annotation,
      clientX: Number(clientX) || 0,
      clientY: Number(clientY) || 0,
      time,
      source
    }
  }

  activateAnnotation(annotation, clientX, clientY, time = performance.now(), source = 'unknown') {
    if (!annotation || !this.annotationManager) return false
    const recent = this.lastAnnotationActivation
    if (
      recent?.annotation === annotation && recent.source !== source &&
      ['pointer', 'touch'].includes(recent.source) && ['pointer', 'touch'].includes(source) &&
      time - recent.time < 160
    ) {
      this.rememberAnnotationActivation(annotation, clientX, clientY, time, source)
      return true
    }
    const handled = this.annotationManager.focusAnnotation?.(annotation) !== false
    if (handled) this.rememberAnnotationActivation(annotation, clientX, clientY, time, source)
    return handled
  }

  activateAnnotationAtClientPoint(clientX, clientY, source = 'unknown') {
    const annotation = this.findAnnotationAtClientPoint(clientX, clientY)
    if (!annotation) return false
    return this.activateAnnotation(annotation, clientX, clientY, performance.now(), source)
  }

  getNextAnnotationLabel() {
    const labels = this.annotationEntities
      .map(entity => Number.parseInt(entity?.script?.annotation?.label, 10))
      .filter(Number.isFinite)
    return labels.length ? Math.max(...labels) + 1 : 1
  }

  async createAnnotationAtClientPoint(clientX, clientY) {
    if (!this.annotationEditMode || this.focusPending || !this.picker) return
    const rect = this.canvas.getBoundingClientRect()
    if (!rect.width || !rect.height) return
    const bufferX = Math.floor(this.softwareLandscape
      ? (clientY - rect.top) * (this.canvas.width / rect.height)
      : (clientX - rect.left) * (this.canvas.width / rect.width))
    const bufferY = Math.floor(this.softwareLandscape
      ? (rect.right - clientX) * (this.canvas.height / rect.width)
      : (clientY - rect.top) * (this.canvas.height / rect.height))
    this.focusPending = true
    try {
      const point = await this.picker.pick(
        bufferX,
        bufferY,
        this.canvas.width,
        this.canvas.height,
        null,
        undefined,
        { depth: Boolean(this.lodSession) }
      )
      if (!point || !this.annotationEditMode) return
      this.emit('onAnnotationCreate', { position: { x: point.x, y: point.y, z: point.z } })
    } catch (error) {
      console.warn('[MeTop viewer] annotation pick failed', error)
      this.emit('onToast', '没有选中可标注的位置')
    } finally {
      this.focusPending = false
    }
  }

  createAnnotation(position, data = {}) {
    if (this.annotationReadOnly) return null
    const label = this.getNextAnnotationLabel()
    const focus = normalizeVector(position)
    if (!focus) return null
    const cameraPosition = this.camera?.getPosition?.()
    const result = this.createAnnotationEntity({
      ...data,
      x: focus.x,
      y: focus.y,
      z: focus.z,
      indexLabel: String(label),
      cameraPose: cameraPosition ? {
        position: { x: cameraPosition.x, y: cameraPosition.y, z: cameraPosition.z },
        focus: { x: focus.x, y: focus.y, z: focus.z }
      } : null
    })
    this.emitAnnotationState()
    return result?.entity?.getGuid?.() || null
  }

  updateAnnotation(id, data = {}) {
    if (this.annotationReadOnly) return false
    const entity = this.findAnnotationEntity(id)
    const annotation = entity?.script?.annotation
    if (!annotation) return false
    annotation.title = String(data.title ?? annotation.title ?? '')
    annotation.text = String(data.text ?? annotation.text ?? '')
    annotation.titleColor = normalizeAnnotationTextColor(data.titleColor ?? annotation.titleColor)
    annotation.textColor = normalizeAnnotationTextColor(data.textColor ?? annotation.textColor)
    annotation.textSize = normalizeAnnotationTextSize(data.textSize ?? annotation.textSize)
    if (Array.isArray(data.attachments)) annotation.attachments = normalizeAnnotationAttachments(data.attachments)
    this.emitAnnotationState()
    return true
  }

  deleteAnnotation(id) {
    if (this.annotationReadOnly) return false
    const entity = this.findAnnotationEntity(id)
    if (!entity) return false
    const index = this.annotationEntities.indexOf(entity)
    if (index >= 0) this.annotationEntities.splice(index, 1)
    if (this.annotationMenuEntity === entity) this.annotationMenuEntity = null
    revokeLocalAnnotationAttachments(entity?.script?.annotation?.attachments)
    entity.destroy()
    this.emitAnnotationState()
    return true
  }

  updateAnnotationCameraPose(id) {
    if (this.annotationReadOnly) return false
    const entity = this.findAnnotationEntity(id)
    const annotation = entity?.script?.annotation
    const cameraPosition = this.camera?.getPosition?.()
    const focus = entity?.getPosition?.()
    if (!annotation || !cameraPosition || !focus) return false
    annotation.cameraPose = {
      position: { x: cameraPosition.x, y: cameraPosition.y, z: cameraPosition.z },
      focus: { x: focus.x, y: focus.y, z: focus.z }
    }
    this.emitAnnotationState()
    return true
  }

  applyAnnotationCameraPose(annotation) {
    const pose = normalizeAnnotationCameraPose(annotation?.cameraPose)
    if (!pose || !this.controls) return false
    this.controls.autoRotate = false
    this.controls.reset(new pc.Vec3(pose.focus.x, pose.focus.y, pose.focus.z), new pc.Vec3(pose.position.x, pose.position.y, pose.position.z), { duration: 0.5 })
    return true
  }

  focusAnnotation(id) {
    const entity = this.findAnnotationEntity(id)
    const annotation = entity?.script?.annotation
    if (!annotation || !this.annotationManager) return false
    this.annotationVisible = true
    entity.enabled = true
    this.applyAnnotationCameraPose(annotation)
    this.annotationManager.focusAnnotation(annotation)
    this.emitAnnotationState({ activeId: entity.getGuid?.() || entity.name })
    return true
  }

  handleAnnotationClick(annotation, event) {
    const entity = annotation?.entity
    if (!entity) return
    const id = entity.getGuid?.() || entity.name
    this.applyAnnotationCameraPose(annotation)
    const hotspot = this.annotationManager?._annotationResources?.get(annotation)?.hotspotDom
    const rect = hotspot?.getBoundingClientRect?.()
    this.annotationMenuEntity = this.annotationEditMode ? entity : null
    this.emit('onAnnotationSelect', {
      id,
      editing: this.annotationEditMode,
      clientX: rect ? rect.left + rect.width / 2 : event?.clientX,
      clientY: rect ? rect.top - rect.height : event?.clientY
    })
    this.emitAnnotationState({ activeId: id })
  }

  handleAnnotationAttachmentPreview(detail) {
    this.emit('onAnnotationAttachmentPreview', detail)
  }

  updateAnnotationMenuPosition() {
    if (!this.annotationEditMode || !this.annotationMenuEntity || !this.annotationManager) return
    const annotation = this.annotationMenuEntity.script?.annotation
    const hotspot = annotation && this.annotationManager._annotationResources?.get(annotation)?.hotspotDom
    const rect = hotspot?.getBoundingClientRect?.()
    if (!rect || rect.width <= 0 || rect.height <= 0) return
    this.emit('onAnnotationMenuPosition', {
      id: this.annotationMenuEntity.getGuid?.() || this.annotationMenuEntity.name,
      clientX: rect.left + rect.width / 2,
      clientY: rect.top - rect.height
    })
  }

  handleAnnotationRemove(annotation) {
    const entity = annotation?.entity
    if (this.annotationMenuEntity === entity) this.annotationMenuEntity = null
    const index = this.annotationEntities.indexOf(entity)
    if (index >= 0) this.annotationEntities.splice(index, 1)
  }

  async persistAnnotationAttachments(attachments, uploadSessionId, onProgress) {
    const normalized = normalizeAnnotationAttachments(attachments)
    const saved = []
    const failed = []
    for (let index = 0; index < normalized.length; index += 1) {
      const item = normalized[index]
      try {
        let result = item
        if (item.file) {
          const uploaded = await this.api.uploadAnnotationAttachment(this.config.taskId, item.file, {
            uploadSessionId,
            signal: this.pendingAnnotationSave?.signal,
            onProgress: progress => onProgress?.({ index, total: normalized.length, progress, name: item.name, status: 'uploading' })
          })
          result = {
            ...uploaded,
            kind: inferAnnotationAttachmentKind('', uploaded.mime, uploaded.name, uploaded.url),
            position: item.position,
            uploadStatus: 'saved',
            uploadProgress: 100
          }
          if (String(item.url || '').startsWith('blob:')) URL.revokeObjectURL(item.url)
        }
        if (!result.fileId) throw new Error(`附件“${result.name || '附件'}”缺少文件引用，请重新上传`)
        saved.push(annotationAttachmentForServer(result))
        onProgress?.({ index, total: normalized.length, progress: 100, name: item.name, status: 'saved' })
      } catch (error) {
        if (error?.name === 'AbortError') throw error
        const message = error?.message || '附件上传失败'
        failed.push({ name: item.name || '附件', error: message })
        onProgress?.({ index, total: normalized.length, progress: 0, name: item.name, status: 'failed', error: message })
        if (String(item.url || '').startsWith('blob:')) URL.revokeObjectURL(item.url)
      }
    }
    return {
      attachments: saved.sort((left, right) => (left.position ?? 0) - (right.position ?? 0)),
      failed
    }
  }

  async saveAnnotations(onProgress) {
    if (this.annotationReadOnly) throw new Error('社区模型为只读模式，不能修改标注')
    if (!this.config.taskId) throw new Error('当前模型没有任务 ID，无法保存标注')
    if (this.pendingAnnotationSave) return false
    const controller = new AbortController()
    this.pendingAnnotationSave = controller
    const uploadSessionId = createAnnotationUploadSessionId()
    let committed = false
    try {
    const data = []
    const annotations = this.getAnnotations()
    let skippedAttachmentCount = 0
    const attachmentErrors = []
      for (let index = 0; index < annotations.length; index += 1) {
        const item = annotations[index]
        onProgress?.({ phase: 'annotation', current: index + 1, total: annotations.length, progress: 0, name: item.title })
        const attachmentResult = await this.persistAnnotationAttachments(item.attachments, uploadSessionId, detail => {
          onProgress?.({ phase: 'attachment', current: index + 1, total: annotations.length, ...detail })
        })
        const attachments = attachmentResult.attachments
        skippedAttachmentCount += attachmentResult.failed.length
        attachmentResult.failed.forEach(item => {
          if (item.error) attachmentErrors.push(`${item.name || '附件'}：${item.error}`)
        })
        const entity = this.findAnnotationEntity(item.id)
        if (entity?.script?.annotation) entity.script.annotation.attachments = attachments
        data.push({
          label: item.title,
          description: item.text,
          titleColor: item.titleColor,
          textColor: item.textColor,
          textSize: item.textSize,
          attachments,
          x: String(item.position.x),
          y: String(item.position.y),
          z: String(item.position.z),
          cameraPose: item.cameraPose
        })
      }
      onProgress?.({ phase: 'saving', current: annotations.length, total: annotations.length, progress: 100 })
      const response = await this.api.saveAnnotations(this.config.taskId, data, uploadSessionId, this.config.activity)
      const backendDiscardedCount = Number(
        response?.data?.data?.discardedAttachmentCount
        ?? response?.data?.discardedAttachmentCount
        ?? response?.discardedAttachmentCount
        ?? 0
      )
      if (Number.isFinite(backendDiscardedCount) && backendDiscardedCount > 0) {
        skippedAttachmentCount += backendDiscardedCount
      }
      committed = true
      this.annotationEntities.forEach(entity => { entity.__fromServer = true })
      this.annotationBaseline = this.serializeAnnotations()
      this.annotationEditSnapshot = this.getAnnotations().map(item => ({
        label: item.label,
        title: item.title,
        text: item.text,
        titleColor: item.titleColor,
        textColor: item.textColor,
        textSize: item.textSize,
        attachments: item.attachments.map(attachment => ({ ...attachment })),
        size: item.size,
        cameraPose: item.cameraPose,
        x: item.position.x,
        y: item.position.y,
        z: item.position.z,
        __fromServer: true
      }))
      this.emitAnnotationState({ saved: true })
      return { saved: true, skippedAttachmentCount, attachmentErrors }
    } finally {
      if (!committed) {
        await this.api.cancelAnnotationAttachmentUpload(this.config.taskId, uploadSessionId).catch(() => {})
      }
      this.pendingAnnotationSave = null
    }
  }

  captureSnapshot() {
    if (!this.loaded) return ''
    try {
      this.app.renderNextFrame = true
      const image = this.canvas.toDataURL('image/png')
      if (!image || image === 'data:,') throw new Error('截图生成失败')
      return image
    } catch (error) {
      this.emit('onToast', error.message || '截图失败')
      return ''
    }
  }

  async captureAiViews({ maxImages = 3, maxEdge = 960, quality = 0.78 } = {}) {
    if (!this.loaded || !this.app || !this.controls || !this.canvas) return []
    const imageCount = pc.math.clamp(Math.round(Number(maxImages) || 3), 1, 3)
    const saved = {
      position: this.controls.pose.position.clone(),
      focus: this.controls.getCurrentFocusPoint(),
      mode: this.controls.mode,
      enabled: this.controls.enabled,
      autoRotate: this.controls.autoRotate
    }
    const distance = Math.max(saved.position.distance(saved.focus), this.bounds?.radius || 0, 0.001)
    const views = [
      { name: 'current', current: true },
      { name: 'front', direction: new pc.Vec3(0, 0, 1) },
      { name: 'right', direction: new pc.Vec3(1, 0, 0) }
    ].slice(0, imageCount)
    const captures = []

    try {
      this.controls.enabled = false
      this.controls.autoRotate = false
      if (this.controls.mode !== 'orbit') this.controls.setMode('orbit')

      for (const view of views) {
        if (view.current) this.controls.setPose(saved.focus, saved.position, { immediate: true })
        else this.controls.lookFrom(view.direction, distance, { immediate: true })
        this.app.render()
        captures.push({
          name: view.name,
          canvas: copyCanvasForUpload(this.canvas, maxEdge)
        })
      }
    } finally {
      this.controls.setPose(saved.focus, saved.position, { immediate: true })
      if (saved.mode !== this.controls.mode) this.controls.setMode(saved.mode)
      this.controls.enabled = saved.enabled
      if (saved.autoRotate && saved.mode === 'orbit') this.controls.autoRotate = true
      this.app.render()
    }

    const safeTitle = String(this.meta?.title || 'metop-space')
      .replace(/[^\p{L}\p{N}._-]+/gu, '-')
      .replace(/^-+|-+$/g, '')
      .slice(0, 48) || 'metop-space'
    return Promise.all(captures.map(({ name, canvas }) => (
      encodeJpeg(canvas, `${safeTitle}-${name}.jpg`, quality)
    )))
  }

  async shareSnapshot(dataUrl) {
    if (!dataUrl) return
    try {
      const response = await fetch(dataUrl)
      const blob = await response.blob()
      const file = new File([blob], `${this.meta?.title || 'metop-space'}.png`, { type: 'image/png' })
      if (navigator.share && navigator.canShare?.({ files: [file] })) {
        await navigator.share({ title: this.meta?.title || 'MeTop.ai 3D 空间', files: [file] })
        return
      }
      const link = document.createElement('a')
      link.href = dataUrl
      link.download = file.name
      link.click()
    } catch (error) {
      if (error.name !== 'AbortError') this.emit('onToast', '请长按图片保存')
    }
  }

  getSharePayload() {
    const shareUrl = new URL(window.location.origin + window.location.pathname)
    const values = {
      taskId: this.config.taskId,
      title: this.meta?.title || this.config.fallbackTitle || '3D 空间',
      imageUrl: this.meta?.preview || this.config.fallbackImageUrl || '',
      format: this.format || this.meta?.modelFormat || this.config.requestedFormat || '',
      pipeline: this.meta?.pipeline || this.config.pipelineHint || '',
      activity: this.config.activity || ''
    }
    Object.entries(values).forEach(([key, value]) => {
      if (String(value || '').trim()) shareUrl.searchParams.set(key, String(value))
    })
    return {
      type: 'shareModel',
      title: values.title,
      taskId: values.taskId,
      imageUrl: values.imageUrl,
      format: values.format,
      pipeline: values.pipeline,
      activity: values.activity,
      url: shareUrl.toString()
    }
  }

  async shareModelPage() {
    const payload = this.getSharePayload()
    if (!payload.taskId) {
      this.emit('onToast', '当前模型暂不支持分享')
      return false
    }

    if (this.config.embedded && window.wx?.miniProgram?.navigateTo) {
      if (this.config.viewMode === 'project') {
        // Projects use the same share composer as community models. The
        // composer creates/renews the owner share id only after the user has
        // chosen the title and cover, then puts that id in the final card.
        const query = [
          `taskId=${encodeURIComponent(payload.taskId)}`,
          `title=${encodeURIComponent(payload.title || '')}`,
          `imageUrl=${encodeURIComponent(payload.imageUrl || '')}`,
          `format=${encodeURIComponent(payload.format || '')}`,
          `pipeline=${encodeURIComponent(payload.pipeline || '')}`,
          'owner=1'
        ].join('&')
        window.wx.miniProgram.navigateTo({ url: `/pages/share/index?${query}` })
        return true
      }
      const query = [
        `taskId=${encodeURIComponent(payload.taskId)}`,
        `title=${encodeURIComponent(payload.title)}`,
        `imageUrl=${encodeURIComponent(payload.imageUrl || '')}`,
        `format=${encodeURIComponent(payload.format || '')}`,
        `pipeline=${encodeURIComponent(payload.pipeline || '')}`,
        // Preserve the access grant when a recipient forwards an already
        // shared project. Without it the next card points at a private task
        // without the share authorization and cannot be opened.
        `shareId=${encodeURIComponent(this.config.shareId || '')}`,
        // Activity entries can point at models that are intentionally absent
        // from the public community index. Preserve the activity scope when
        // handing the share off to the miniapp share composer.
        `activity=${encodeURIComponent(this.config.activity || '')}`
      ].join('&')
      window.wx.miniProgram.navigateTo({ url: `/pages/share/index?${query}` })
      return true
    }

    try {
      if (navigator.share) {
        await navigator.share({
          title: payload.title,
          text: `来自 MeTop.ai 的 3D 空间：${payload.title}`,
          url: payload.url
        })
        return true
      }
      await navigator.clipboard.writeText(payload.url)
      this.emit('onToast', '模型链接已复制')
      return true
    } catch (error) {
      if (error?.name === 'AbortError') return false
      try {
        const input = document.createElement('textarea')
        input.value = payload.url
        input.setAttribute('readonly', '')
        input.style.position = 'fixed'
        input.style.opacity = '0'
        document.body.appendChild(input)
        input.select()
        document.execCommand('copy')
        input.remove()
        this.emit('onToast', '模型链接已复制')
        return true
      } catch {
        this.emit('onToast', '请使用浏览器分享当前页面')
        return false
      }
    }
  }

  openArProjection() {
    if (!supportsArProjection(this.task)) {
      const pipeline = String(this.task?.pipeline_type ?? this.task?.pipelineType ?? '').trim().toLowerCase()
      this.emit('onToast', pipeline === 'direct_upload'
        ? '用户上传作品不支持 AR 投影'
        : '仅去背景模型支持 AR 投影')
      return false
    }
    const payload = this.getSharePayload()
    if (!payload.taskId) {
      this.emit('onToast', '当前模型暂不支持 AR 投影')
      return false
    }
    if (!this.config.embedded || !window.wx?.miniProgram?.navigateTo) {
      this.emit('onToast', '请在微信小程序中使用 AR 投影')
      return false
    }

    const query = [
      `taskId=${encodeURIComponent(payload.taskId)}`,
      `title=${encodeURIComponent(payload.title)}`,
      // Reuse the format selected by the regular viewer. AR supports SOG and
      // PLY; TAR/LOD is passed through so the miniapp can try an AR-capable
      // sibling artifact instead of blindly requesting SOG.
      `format=${encodeURIComponent(payload.format || '')}`,
      `formats=${encodeURIComponent(JSON.stringify(resolveAvailableModelFormats(this.task)))}`,
      `pipeline=${encodeURIComponent(payload.pipeline || '')}`,
      `view=${encodeURIComponent(this.config.viewMode || '')}`,
      `activity=${encodeURIComponent(this.config.activity || '')}`,
      `shareId=${encodeURIComponent(this.config.shareId || '')}`
    ].join('&')
    window.wx.miniProgram.navigateTo({
      url: `/pages/ar-projector/index?${query}`,
      fail: () => this.emit('onToast', 'AR 投影页打开失败，请重试')
    })
    return true
  }

  destroy() {
    this.stopCameraTrajectory()
    if (this.videoRecording) void this.cancelVideoRecording({ silent: true })
    this.clearRecordedVideo()
    this.imageVideoLookupController?.abort()
    this.imageVideoLookupController = null
    this.imageVideoGeneration?.controller?.abort()
    this.imageVideoGeneration = null
    this.generatedImageVideo = null
    if (this.destroyed) return
    this.destroyed = true
    window.clearTimeout(this.readyTimer)
    window.clearTimeout(this.tapTimer)
    window.removeEventListener('resize', this.resize)
    document.removeEventListener('visibilitychange', this.handleVisibilityChange)
    this.canvas.removeEventListener('pointerdown', this.handlePointerDown, true)
    this.canvas.removeEventListener('pointercancel', this.handlePointerCancel, true)
    this.canvas.removeEventListener('pointerup', this.handlePointerUp, true)
    this.canvas.removeEventListener('dblclick', this.handleDoubleClick, true)
    this.root.removeEventListener('touchstart', this.handleAnnotationTouchStart, true)
    this.root.removeEventListener('touchmove', this.handleAnnotationTouchMove, true)
    this.root.removeEventListener('touchend', this.handleAnnotationTouchEnd, true)
    this.root.removeEventListener('touchcancel', this.handleAnnotationTouchCancel, true)
    this.root.removeEventListener('click', this.handleAnnotationClickCapture, true)
    this.resizeObserver?.disconnect()
    this.resizeObserver = null
    this.entrance?.destroy()
    this.entrance = null
    this.surfaceRing = clearSurfaceRingMarker(this.surfaceRing)
    this.pendingAnnotationSave?.abort?.()
    this.pendingAnnotationSave = null
    this.app?.off('annotation:click', this.handleAnnotationClick)
    this.app?.off('annotation:attachmentPreview', this.handleAnnotationAttachmentPreview)
    this.app?.off('annotation:remove', this.handleAnnotationRemove)
    this.picker?.destroy()
    this.picker = null
    this.controls?.destroy()
    this.controls = null
    this.clearModel()
    this.clearSkyboxes()
    this.app?.destroy?.()
    this.app = null
  }
}
